/* eslint-disable no-undef */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { useContext, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { GlobalContext } from 'providers/GlobalProvider';
import EmailAPI from 'api/EmailAPI';
import GroupAPI from 'api/GroupAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import TeamsAPI from 'api/TeamsAPI';
import CreditsAPI from 'api/CreditsAPI';
import PendingInviteAPI from 'api/PendingInviteAPI';
import UsersAPI from 'api/UsersAPI';
import { useSnackbar } from 'notistack';
import KeycloakService from 'services/KeycloakService';
import { inviteUserNotification } from 'utils/NotificationsFuncs';

const TRIAL_INCLUDED_ITEMS = [
  {
    key: 'workflows',
    title: 'AI workflows',
    description: '1 free run each of Import, Export and Beneficial Ownership',
    linkText: 'Try a workflow',
    linkTo: '/import-page',
    iconPath:
      'M19 15.18V7c0-2.21-1.79-4-4-4s-4 1.79-4 4v10c0 1.1-.9 2-2 2s-2-.9-2-2V8.82C8.16 8.4 9 7.3 9 6c0-1.66-1.34-3-3-3S3 4.34 3 6c0 1.3.84 2.4 2 2.82V17c0 2.21 1.79 4 4 4s4-1.79 4-4V7c0-1.1.9-2 2-2s2 .9 2 2v8.18c-1.16.41-2 1.51-2 2.82 0 1.66 1.34 3 3 3s3-1.34 3-3c0-1.3-.84-2.4-2-2.82M6 7c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1m12 12c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1',
  },
  // TEMPORARILY DISABLED: free 25 analyzer credits on trial.
  // {
  //   key: 'analyzers',
  //   title: 'Analyzers',
  //   description: '25 credits for the ACE and Tariff Analyzers',
  //   linkText: 'Run an analysis',
  //   linkTo: '/ace-analyzers',
  //   iconPath:
  //     'm22 20.59-4.69-4.69C18.37 14.55 19 12.85 19 11c0-4.42-3.58-8-8-8-4.08 0-7.44 3.05-7.93 7h2.02C5.57 7.17 8.03 5 11 5c3.31 0 6 2.69 6 6s-2.69 6-6 6c-2.42 0-4.5-1.44-5.45-3.5H3.4C4.45 16.69 7.46 19 11 19c1.85 0 3.55-.63 4.9-1.69L20.59 22zM8.43 9.69 9.65 15h1.64l1.26-3.78.95 2.28h2V12h-1l-1.25-3h-1.54l-1.12 3.37L9.35 7H7.7l-1.25 4H1v1.5h6.55z',
  // },
  {
    key: 'bitedb',
    title: 'BITE DB',
    description: 'Up to 100 product and entity records',
    linkText: 'Add records',
    linkTo: '/bite-db',
    iconPath:
      'M19 15v4H5v-4zm1-2H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1v-6c0-.55-.45-1-1-1M7 18.5c-.82 0-1.5-.67-1.5-1.5s.68-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5M19 5v4H5V5zm1-2H4c-.55 0-1 .45-1 1v6c0 .55.45 1 1 1h16c.55 0 1-.45 1-1V4c0-.55-.45-1-1-1M7 8.5c-.82 0-1.5-.67-1.5-1.5S6.18 5.5 7 5.5s1.5.68 1.5 1.5S7.83 8.5 7 8.5',
  },
];

interface ILoginWizard {
  cur_user: any;
  onTeamCreated?: () => void;
}

const LoginWizard: React.FC<ILoginWizard> = ({ cur_user, onTeamCreated }) => {
  const [step, setStep] = useState<number>(1);
  const [teamOpt, setTeamOpt] = useState<string>('');
  const [newTeamName, setNewTeamName] = useState<string>('');
  const [teamMembers, setTeamMembers] = useState<string[]>([]);
  const [teamMemberInput, setTeamMemberInput] = useState<string>('');
  const [acctCreating, setAcctCreating] = useState<boolean>(false);
  const [trialEndSeconds, setTrialEndSeconds] = useState<number | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { refreshCurUser } = useContext(GlobalContext);
  const teamMembersInputRef = useRef<HTMLInputElement | null>(null);

  const biteScoreRoleId = process.env.REACT_APP_BITE_SCORE_ID!;
  const biteVizRoleId = process.env.REACT_APP_BITE_VIZ_ID!;
  const businessPrice = process.env.REACT_APP_BUSINESS_PRICE!;

  const trialEndMoment = trialEndSeconds ? moment.unix(trialEndSeconds) : null;
  const trialDaysLeft = trialEndMoment
    ? Math.max(0, Math.ceil(trialEndMoment.diff(moment(), 'hours', true) / 24))
    : null;
  const trialEndLabel = trialEndMoment
    ? trialEndMoment.format('MMM D, YYYY')
    : null;

  const sanitizeEmail = (value: string): string =>
    value.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '').trim();

  const validateEmail = (email: string): boolean => {
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return Boolean(email) && isValidEmail.test(email);
  };

  const getCurrentTeamMemberInputValue = () => {
    const liveInputValue = teamMembersInputRef.current?.value;
    if (
      typeof liveInputValue === 'string' &&
      sanitizeEmail(liveInputValue) !== ''
    ) {
      return liveInputValue;
    }

    return teamMemberInput;
  };

  const getInviteEmails = (): string[] => {
    const pendingInputEmails = normalizeTeamMemberEmails(
      getCurrentTeamMemberInputValue(),
    );
    const allEmails = [...teamMembers, ...pendingInputEmails].map((email) =>
      sanitizeEmail(email),
    );

    return allEmails.filter(
      (email, index) =>
        email !== '' &&
        allEmails.findIndex(
          (currentEmail) =>
            currentEmail.toLowerCase() === email.toLowerCase(),
        ) === index,
    );
  };

  const sendTeamInvites = async (
    teamId: string,
    teamName: string,
    emails: string[],
  ) => {
    const validEmails = emails.filter((email) => validateEmail(email));
    const invalidEmails = emails.filter((email) => !validateEmail(email));

    if (invalidEmails.length > 0) {
      enqueueSnackbar(
        invalidEmails.length === 1
          ? `Skipped invalid email: ${invalidEmails[0]}`
          : `Skipped ${invalidEmails.length} invalid emails`,
        { variant: 'warning' },
      );
    }

    const inviteResults = await Promise.all(
      validEmails.map(async (email) => {
        try {
          const inviteResp = await EmailAPI.inviteUserToTeam({
            requestorEmail: cur_user?.basics?.email,
            teamID: teamId,
            teamName: teamName,
            formValues: { email: email },
          });

          if (!inviteResp?.success) {
            const message: string =
              inviteResp &&
              inviteResp.result &&
              typeof inviteResp.result === 'string'
                ? inviteResp.result
                : `Unable to send team invite to ${email}`;
            enqueueSnackbar(message, { variant: 'error' });
            return false;
          }

          const [pendingResp, userResp] = await Promise.all([
            PendingInviteAPI.addPendingInvite(teamId, email.toLowerCase()),
            UsersAPI.searchUsers(email),
          ]);

          if (!pendingResp?.success) {
            enqueueSnackbar(
              'Invite email sent, but pending invite record was not saved.',
              {
                variant: 'warning',
              },
            );
          }

          if (userResp?.success && userResp.result.length > 0) {
            const userId = userResp.result[0].id;
            await inviteUserNotification(
              cur_user,
              {
                id: teamId,
                name: teamName,
              },
              userId,
            );
          } else if (!userResp?.success) {
            enqueueSnackbar('Unable to send invite notification to user', {
              variant: 'error',
            });
          }

          return true;
        } catch {
          enqueueSnackbar(`Unable to send team invite to ${email}`, {
            variant: 'error',
          });
          return false;
        }
      }),
    );

    const successfulInvites = inviteResults.filter(Boolean).length;

    if (successfulInvites > 0) {
      enqueueSnackbar(
        successfulInvites === 1
          ? 'Team invite sent via Email'
          : `${successfulInvites} team invites sent via Email`,
        { variant: 'success' },
      );
    }
  };

  const handleSubmit = async (value: any) => {
    if (value === '') {
      return;
    }
    const inviteEmails = getInviteEmails();
    // removes any extra whitespaces in team name from input field
    const formattedName = value.trim();
    const new_team: any = {
      name: formattedName,
      team_admin_id: cur_user?.basics?.email,
      team_id: '',
      keycloakUserId: cur_user?.basics?.id,
    };
    setAcctCreating(true);

    const failStep = (message: string) => {
      enqueueSnackbar(message, { variant: 'error' });
      setAcctCreating(false);
    };

    try {
      const resp = await TeamsAPI.createTeam({ newTeam: new_team });
      if (!resp?.success || !cur_user?.basics?.id) {
        failStep('Error creating new team. Team name may already be in use');
        return;
      }
      const teamId = resp.result.id;
      new_team.team_id = teamId;

      const addUser = await GroupAPI.assignGroupToUser(
        teamId,
        cur_user?.basics?.id,
      );
      if (!addUser?.success) {
        failStep(
          'Unable to add you to the new team. Please try again or contact support.',
        );
        return;
      }

      const createStripeCustomer = await PaymentsAPI.createCustomer(new_team);
      if (!createStripeCustomer?.success) {
        failStep(
          'Unable to set up billing for the new team. Please try again or contact support.',
        );
        return;
      }

      const autoStartTrial = await PaymentsAPI.autoStartTrial(
        cur_user?.basics?.id,
        createStripeCustomer?.result?.id,
        businessPrice,
        {
          id: teamId,
          admin: cur_user?.basics?.email,
          name: formattedName,
        },
      );
      if (!autoStartTrial?.success) {
        failStep(
          'Unable to start your free trial. Please try again or contact support.',
        );
        return;
      }
      setTrialEndSeconds(autoStartTrial?.result?.trial_end ?? null);

      const seedTrialCredits = await CreditsAPI.seedTrialCredits({
        teamId: teamId,
      });
      if (!seedTrialCredits?.success) {
        failStep('Unable to initialize free trial module credits.');
        return;
      }

      const addBiteScoreRole = await GroupAPI.addRoleToGroup(
        teamId,
        biteScoreRoleId,
        'bite-score',
      );
      if (!addBiteScoreRole?.success) {
        failStep(
          'Unable to finish setting up team permissions. Please try again or contact support.',
        );
        return;
      }

      const addBiteVizRole = await GroupAPI.addRoleToGroup(
        teamId,
        biteVizRoleId,
        'customizable',
      );
      if (!addBiteVizRole?.success) {
        failStep(
          'Unable to finish setting up team permissions. Please try again or contact support.',
        );
        return;
      }

      await sendTeamInvites(teamId, formattedName, inviteEmails);
      // onTeamCreated flags the parent to keep this modal mounted by its own
      // state rather than `requiresTeamOnboarding(cur_user)` -- that check
      // flips false the instant the refresh below resolves, which would
      // otherwise unmount this wizard (and step 4 with it) mid-read.
      onTeamCreated?.();
      // Refresh cur_user (no reload) so it picks up the new team/license
      // before step 4's links are shown -- otherwise route guards
      // (requiresTeamOnboarding) still see a team-less user and remount this
      // wizard at step 1, which looks like the wizard "restarting".
      // Uses refreshCurUser, not initKeycloakUser -- the latter re-runs
      // KeycloakService.initKeycloak, whose underlying _kc.init() can only be
      // called once per page load and throws on a second call.
      await refreshCurUser();
      setAcctCreating(false);
      setStep(4);
    } catch (err) {
      failStep(
        'Something went wrong while setting up your team. Please try again or contact support.',
      );
    }
  };

  const handleStep = (action: string) => {
    if (action === 'increase') {
      if (step < 3) {
        setStep(step + 1);
      }
      if (step >= 3) {
        setStep(3);
      }
    }
    if (action === 'decrease') {
      if (step > 0) {
        setStep(step - 1);
      }
      if (step === 1) {
        setStep(1);
      }
    }
  };

  const handleTeamYesSelect = () => {
    setTeamOpt('yes');
  };

  const handleTeamNoSelect = () => {
    setTeamOpt('no');
  };

  const normalizeTeamMemberEmails = (value: string) => {
    return value
      .split(/[\s,;]+/)
      .map((member) => sanitizeEmail(member))
      .filter(Boolean);
  };

  const handleAddTeamMember = () => {
    const nextMembers = normalizeTeamMemberEmails(
      getCurrentTeamMemberInputValue(),
    );

    if (nextMembers.length === 0) {
      teamMembersInputRef.current?.focus();
      return;
    }

    setTeamMembers((prevMembers) => {
      const existingMembers = new Set(
        prevMembers.map((member) => member.toLowerCase()),
      );
      const uniqueMembers = nextMembers.filter(
        (member) => !existingMembers.has(member.toLowerCase()),
      );

      return [...prevMembers, ...uniqueMembers];
    });
    setTeamMemberInput('');
    teamMembersInputRef.current?.focus();
  };

  const handleRemoveTeamMember = (index: number) => {
    setTeamMembers((prevMembers) =>
      prevMembers.filter((_, memberIndex) => memberIndex !== index),
    );
  };

  const handleTeamMemberKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTeamMember();
      return;
    }

    if (
      e.key === 'Backspace' &&
      teamMemberInput === '' &&
      teamMembers.length > 0
    ) {
      setTeamMembers((prevMembers) => prevMembers.slice(0, -1));
    }
  };

  return (
    <div className='login-wizard-container'>
      <div className='welcome-header'>
        {step === 1 && (
          <div className='welcome-header-text'>Welcome to BITE!</div>
        )}
        {step === 2 && (
          <div className='welcome-header-text'>
            Do you already have a team invite?
          </div>
        )}
        {step === 3 && teamOpt === 'yes' && (
          <div className='welcome-header-text'>Go to your invite email.</div>
        )}
        {step === 3 && teamOpt === 'no' && !acctCreating && (
          <div className='welcome-header-text'>Create a new team</div>
        )}
        {step === 3 && teamOpt === 'no' && acctCreating && (
          <div className='welcome-header-text'>Setting up dashboard...</div>
        )}
      </div>
      {step === 1 && (
        <div className='wizard-main-graphic-container'>
          <div className='graphic-wrap'>
            <div className='graphic-group'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='333'
                height='208'
                viewBox='0 0 333 208'
                fill='none'
              >
                <g opacity='0.5'>
                  <path
                    d='M196.861 71.0758L190.953 69.9783C191.278 66.0052 191.043 62.0878 190.308 58.3122L195.845 56.356C197.358 55.8217 198.161 54.126 197.639 52.5646L196.249 48.4016C195.725 46.8312 194.066 45.98 192.539 46.5026L187.082 48.3734C185.391 44.7297 183.196 41.32 180.562 38.2463L184.313 33.9107C185.389 32.6671 185.252 30.7383 184.004 29.5984L180.669 26.5504C179.408 25.398 177.498 25.4727 176.405 26.7221L172.657 31.0062C169.323 28.6219 165.607 26.6549 161.567 25.2172L162.641 19.5044C162.958 17.8195 161.839 16.1296 160.136 15.7289L155.579 14.657C153.855 14.2513 152.193 15.3033 151.873 17.0065L150.787 22.7823C146.429 22.3343 142.11 22.5226 137.941 23.2842L135.854 17.4876C135.241 15.7844 133.35 14.8138 131.628 15.324L127.02 16.6887C125.278 17.2047 124.372 19.0191 125 20.7364L127.176 26.6748C123.234 28.4792 119.571 30.8378 116.31 33.6793L111.171 29.3511C109.714 28.1241 107.532 28.2096 106.3 29.5478L103.012 33.1176C101.771 34.4649 101.966 36.5506 103.443 37.7717L108.775 42.1762C106.412 45.6622 104.533 49.5216 103.249 53.6846L96.1099 52.3581C94.1881 52.0013 92.3527 53.1628 92.0163 54.9531L91.1211 59.7151C90.7847 61.5054 92.0778 63.231 94.0029 63.567L101.346 64.8496C101.184 69.1196 101.663 73.3042 102.708 77.3013L95.5526 79.7545C93.728 80.38 92.7879 82.2616 93.4573 83.9531L95.232 88.4355C95.8973 90.1155 97.9046 90.9559 99.7126 90.3171L106.965 87.7544C108.998 91.3317 111.528 94.6154 114.476 97.5057L109.505 103.189C108.293 104.574 108.48 106.585 109.92 107.675L113.73 110.557C115.154 111.635 117.273 111.387 118.466 110.009L123.412 104.293C126.86 106.286 130.613 107.845 134.604 108.889L133.26 116.043C132.939 117.747 134.11 119.309 135.869 119.53L140.517 120.118C142.253 120.337 143.913 119.149 144.23 117.463L145.559 110.386C149.611 110.452 153.566 109.979 157.343 109.035L159.717 115.516C160.284 117.064 162.041 117.8 163.639 117.163L167.863 115.478C169.443 114.847 170.273 113.093 169.719 111.555L167.428 105.191C170.891 103.374 174.094 101.12 176.95 98.498L181.984 102.657C183.22 103.678 185.08 103.461 186.141 102.176L188.953 98.7701C190.008 97.4933 189.875 95.6292 188.655 94.6021L183.799 90.5129C185.918 87.3413 187.637 83.8635 188.876 80.1386L194.955 81.2005C196.522 81.4742 198.034 80.3916 198.336 78.7838L199.14 74.5071C199.442 72.8993 198.423 71.3604 196.859 71.07L196.861 71.0758ZM142.431 97.3141C124.833 94.6842 112.756 78.3931 116.022 61.0118C119.288 43.6297 136.495 32.6373 153.89 36.3398C170.695 39.9163 181.417 55.9627 178.348 72.2896C175.28 88.6164 159.43 99.8552 142.431 97.3149V97.3141Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M137.436 16.1835L133.256 15.2502L131.098 18.7454L137.436 16.1835Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M178.818 25.7448L183.66 26.7022L182.988 30.2762L178.818 25.7448Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M194.063 46.4512L198.862 47.5537L197.144 50.0318L194.063 46.4512Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M197.27 71.2053L202.081 72.5069L200.04 74.5743L197.27 71.2053Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M182.856 103.152L187.468 104.717L188.774 100.962L182.856 103.152Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M161.449 117.188L165.558 118.758L166.486 115.683L161.449 117.188Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M138.759 120.732L135.066 119.34L139.271 115.079L138.759 120.732Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M118.837 112.747L114.561 110.994L118.775 107.484L118.837 112.747Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M108.799 28.4634L113.413 29.4789L110.941 32.5153L108.799 28.4634Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M92.7297 63.0999L96.8665 64.526L97.1812 60.9844L92.7297 63.0999Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M97.084 90.2665L101.115 91.7225L101.971 89.0329L97.084 90.2665Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M201.561 72.3543L195.604 71.2476C195.932 67.2422 195.695 63.2932 194.953 59.4861L200.536 57.5141C202.062 56.9749 202.87 55.2659 202.345 53.6921L200.943 49.4951C200.415 47.9122 198.741 47.0535 197.202 47.5811L191.7 49.4669C189.995 45.7941 187.782 42.3554 185.125 39.2568L188.907 34.8864C189.993 33.632 189.854 31.6882 188.596 30.5392L185.233 27.4663C183.962 26.3048 182.037 26.3803 180.935 27.6397L177.156 31.9586C173.795 29.5544 170.048 27.5716 165.974 26.1215L167.056 20.3623C167.376 18.6632 166.247 16.96 164.531 16.556L159.936 15.4758C158.197 15.0668 156.522 16.1271 156.199 17.8444L155.104 23.6666C150.709 23.2153 146.356 23.4045 142.152 24.1727L140.047 18.3289C139.429 16.6116 137.523 15.6335 135.785 16.1478L131.139 17.5242C129.382 18.0443 128.469 19.8736 129.103 21.6042L131.296 27.5907C127.323 29.4101 123.629 31.7877 120.341 34.6516L115.159 30.2878C113.69 29.0508 111.49 29.1363 110.247 30.4852L106.932 34.0841C105.681 35.4422 105.877 37.5453 107.367 38.7764L112.743 43.2174C110.36 46.7316 108.466 50.6225 107.171 54.8195L99.9734 53.4822C98.0358 53.1221 96.1855 54.2927 95.8458 56.098L94.9439 60.899C94.605 62.7042 95.9089 64.4431 97.8498 64.7824L105.255 66.0758C105.091 70.3806 105.573 74.5992 106.628 78.6287L99.4136 81.1018C97.5741 81.7323 96.6265 83.6296 97.3017 85.3344L99.0905 89.8534C99.7608 91.5466 101.786 92.3945 103.608 91.7507L110.921 89.1673C112.971 92.7745 115.522 96.0838 118.494 98.9982L113.481 104.727C112.26 106.123 112.448 108.151 113.901 109.25L117.742 112.155C119.178 113.242 121.316 112.992 122.517 111.603L127.504 105.84C130.981 107.85 134.765 109.422 138.789 110.473L137.434 117.685C137.111 119.403 138.292 120.978 140.065 121.202L144.751 121.794C146.502 122.016 148.176 120.817 148.495 119.118L149.836 111.984C153.922 112.051 157.909 111.574 161.716 110.622L164.109 117.156C164.682 118.717 166.453 119.459 168.063 118.817L172.323 117.118C173.916 116.482 174.752 114.714 174.194 113.164L171.884 106.748C175.375 104.915 178.604 102.643 181.484 99.9996L186.56 104.192C187.806 105.222 189.682 105.003 190.752 103.708L193.587 100.274C194.65 98.9874 194.516 97.1075 193.287 96.0722L188.391 91.9498C190.528 88.7525 192.261 85.2465 193.509 81.4908L199.639 82.5619C201.219 82.8381 202.743 81.7464 203.047 80.1253L203.858 75.8138C204.163 74.1927 203.135 72.6413 201.558 72.3485L201.561 72.3543ZM146.681 98.8057C128.937 96.1543 116.759 79.7296 120.051 62.2064C123.345 44.6833 140.693 33.6013 158.233 37.3337C175.177 40.9392 185.988 57.1167 182.895 73.5771C179.801 90.0367 163.82 101.367 146.68 98.8057H146.681Z'
                    fill='#A2C6F0'
                  />
                </g>
                <g opacity='0.5'>
                  <path
                    d='M180.696 172.346L177.546 171.761C177.72 169.643 177.594 167.554 177.202 165.542L180.155 164.499C180.961 164.213 181.389 163.31 181.111 162.478L180.371 160.259C180.091 159.422 179.206 158.968 178.393 159.247L175.484 160.244C174.582 158.302 173.412 156.483 172.007 154.845L174.007 152.533C174.581 151.87 174.508 150.842 173.843 150.234L172.065 148.609C171.393 147.995 170.375 148.035 169.792 148.7L167.794 150.984C166.016 149.713 164.035 148.665 161.881 147.898L162.453 144.853C162.622 143.954 162.026 143.053 161.118 142.84L158.689 142.268C157.769 142.052 156.883 142.613 156.713 143.521L156.134 146.6C153.81 146.361 151.508 146.461 149.285 146.868L148.172 143.778C147.845 142.869 146.838 142.352 145.919 142.624L143.462 143.352C142.533 143.627 142.051 144.594 142.386 145.51L143.545 148.676C141.444 149.638 139.491 150.895 137.753 152.41L135.013 150.103C134.236 149.448 133.073 149.494 132.416 150.207L130.663 152.11C130.002 152.829 130.105 153.94 130.893 154.592L133.736 156.94C132.476 158.799 131.474 160.856 130.789 163.076L126.983 162.369C125.959 162.179 124.98 162.798 124.8 163.753L124.323 166.292C124.143 167.247 124.833 168.166 125.859 168.346L129.774 169.029C129.688 171.306 129.943 173.537 130.5 175.668L126.685 176.976C125.712 177.31 125.211 178.313 125.568 179.215L126.514 181.605C126.868 182.501 127.939 182.949 128.902 182.608L132.769 181.241C133.853 183.149 135.201 184.899 136.774 186.441L134.123 189.47C133.476 190.209 133.576 191.281 134.344 191.862L136.376 193.398C137.135 193.973 138.265 193.841 138.901 193.106L141.538 190.059C143.377 191.122 145.378 191.953 147.505 192.51L146.789 196.324C146.617 197.232 147.242 198.065 148.18 198.184L150.658 198.497C151.584 198.614 152.469 197.98 152.638 197.082L153.347 193.309C155.507 193.344 157.616 193.092 159.629 192.589L160.895 196.044C161.197 196.87 162.134 197.262 162.986 196.922L165.238 196.023C166.08 195.687 166.523 194.752 166.227 193.933L165.006 190.54C166.852 189.571 168.559 188.369 170.082 186.971L172.767 189.189C173.425 189.733 174.418 189.617 174.983 188.933L176.482 187.117C177.044 186.436 176.974 185.442 176.324 184.895L173.735 182.715C174.864 181.024 175.781 179.17 176.442 177.184L179.682 177.75C180.518 177.896 181.323 177.319 181.484 176.462L181.913 174.182C182.074 173.325 181.531 172.505 180.697 172.349L180.696 172.346ZM151.677 186.335C142.295 184.933 135.856 176.248 137.597 166.981C139.339 157.714 148.512 151.854 157.787 153.827C166.746 155.734 172.463 164.289 170.827 172.994C169.19 181.698 160.74 187.691 151.677 186.336V186.335Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M149.013 143.081L146.785 142.582L145.634 144.446L149.013 143.081Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M171.076 148.178L173.658 148.688L173.3 150.594L171.076 148.178Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M179.204 159.218L181.763 159.805L180.846 161.126L179.204 159.218Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M180.914 172.414L183.478 173.109L182.39 174.211L180.914 172.414Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M173.229 189.447L175.687 190.282L176.384 188.279L173.229 189.447Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M161.816 196.93L164.006 197.767L164.501 196.127L161.816 196.93Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M149.719 198.82L147.75 198.078L149.992 195.805L149.719 198.82Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M139.097 194.562L136.818 193.628L139.065 191.757L139.097 194.562Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M133.746 149.627L136.206 150.168L134.888 151.787L133.746 149.627Z'
                    fill='#B98DFF'
                  />
                  <path
                    d='M125.179 168.094L127.384 168.853L127.553 166.965L125.179 168.094Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M127.5 182.577L129.65 183.354L130.106 181.919L127.5 182.577Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M183.201 173.027L180.025 172.437C180.2 170.302 180.074 168.196 179.679 166.167L182.655 165.116C183.469 164.829 183.899 163.917 183.62 163.079L182.872 160.841C182.591 159.997 181.699 159.539 180.878 159.821L177.945 160.826C177.035 158.868 175.855 157.035 174.439 155.383L176.456 153.053C177.034 152.384 176.961 151.348 176.29 150.735L174.496 149.096C173.819 148.477 172.792 148.517 172.204 149.188L170.19 151.491C168.397 150.21 166.4 149.153 164.228 148.379L164.805 145.309C164.976 144.403 164.374 143.495 163.459 143.28L161.009 142.704C160.082 142.486 159.189 143.051 159.017 143.967L158.434 147.071C156.091 146.831 153.77 146.932 151.528 147.341L150.406 144.225C150.077 143.309 149.06 142.788 148.134 143.062L145.657 143.795C144.72 144.072 144.233 145.048 144.571 145.97L145.74 149.162C143.622 150.132 141.653 151.399 139.9 152.927L137.137 150.6C136.353 149.941 135.181 149.986 134.518 150.706L132.751 152.625C132.084 153.349 132.188 154.47 132.982 155.126L135.848 157.494C134.579 159.367 133.568 161.442 132.878 163.679L129.04 162.967C128.007 162.775 127.02 163.399 126.839 164.361L126.358 166.92C126.177 167.883 126.872 168.81 127.907 168.991L131.855 169.681C131.767 171.976 132.025 174.224 132.587 176.373L128.74 177.691C127.759 178.027 127.254 179.04 127.614 179.948L128.568 182.357C128.925 183.26 130.005 183.712 130.977 183.368L134.875 181.991C135.968 183.914 137.328 185.679 138.913 187.233L136.24 190.287C135.589 191.032 135.69 192.113 136.464 192.699L138.512 194.248C139.278 194.827 140.417 194.694 141.058 193.954L143.717 190.881C145.57 191.953 147.588 192.791 149.734 193.352L149.011 197.197C148.839 198.113 149.469 198.953 150.414 199.072L152.912 199.388C153.845 199.506 154.738 198.867 154.908 197.961L155.623 194.158C157.802 194.194 159.928 193.939 161.958 193.433L163.234 196.916C163.539 197.748 164.484 198.144 165.342 197.801L167.613 196.895C168.463 196.557 168.909 195.614 168.611 194.787L167.379 191.367C169.24 190.389 170.962 189.178 172.497 187.77L175.204 190.005C175.868 190.554 176.868 190.438 177.439 189.747L178.95 187.916C179.517 187.23 179.445 186.228 178.79 185.676L176.18 183.479C177.319 181.774 178.244 179.905 178.909 177.903L182.177 178.474C183.019 178.621 183.832 178.039 183.994 177.175L184.426 174.876C184.589 174.011 184.041 173.184 183.2 173.028L183.201 173.027ZM153.942 187.129C144.482 185.715 137.989 176.959 139.745 167.617C141.501 158.274 150.75 152.366 160.101 154.356C169.135 156.278 174.899 164.904 173.25 173.679C171.601 182.454 163.08 188.495 153.942 187.129H153.942Z'
                    fill='#A2C6F0'
                  />
                </g>
                <g filter='url(#filter0_d_4238_18080)'>
                  <path
                    d='M147.515 109.226C155.558 109.226 162.077 100.073 162.077 88.7831C162.077 77.493 155.558 68.3406 147.515 68.3406C139.473 68.3406 132.953 77.493 132.953 88.7831C132.953 100.073 139.473 109.226 147.515 109.226Z'
                    fill='#A2C6F0'
                  />
                  <path
                    d='M103.009 39.2485H75.6007C47.4983 39.2485 24.716 62.0065 24.716 90.079C24.716 105.059 36.8729 117.203 51.8684 117.203H126.74C141.737 117.203 153.893 105.059 153.893 90.0799C153.893 62.0065 131.11 39.2493 103.008 39.2493L103.009 39.2485Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M98.1952 172.785H80.4143C62.1823 172.785 47.4019 158.02 47.4019 139.808C47.4019 130.089 55.2884 122.211 65.0176 122.211H113.593C123.322 122.211 131.208 130.09 131.208 139.808C131.208 158.021 116.428 172.785 98.1961 172.785H98.1952Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M127.886 106.239H75.7884C68.7283 106.239 62.9356 100.833 62.9356 94.1641V83.9382C62.9356 77.2698 68.7283 72.0482 75.7884 72.273L127.886 73.933C134.192 74.1338 139.246 79.3189 139.246 85.5153V95.0177C139.246 101.215 134.192 106.238 127.886 106.238V106.239Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M92.4806 88.5086C92.4806 92.962 88.6852 96.5243 83.9655 96.4646C79.2034 96.4049 75.3109 92.6857 75.3109 88.1593C75.3109 83.6329 79.2026 80.0722 83.9655 80.2058C88.6852 80.3377 92.4806 84.0544 92.4806 88.5077V88.5086Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M127.688 89.2237C127.688 93.5278 124.143 96.9723 119.738 96.9168C115.293 96.8603 111.661 93.2706 111.661 88.8985C111.661 84.5265 115.293 81.0835 119.738 81.208C124.144 81.3316 127.688 84.9197 127.688 89.2237Z'
                    fill='var(--text-primary)'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M108.211 110.073C100.463 100.979 103.906 93.7061 93.7762 81.3822C79.8387 64.4248 63.5792 66.3437 59.8544 54.8428C58.8221 51.6562 58.7042 47.2817 61.4722 41.2354C40.2439 47.3522 24.716 66.9029 24.716 90.079C24.716 105.059 36.8729 117.203 51.8684 117.203H117.118C113.21 115.075 110.354 112.589 108.21 110.074L108.211 110.073Z'
                      fill='var(--text-primary)'
                    />
                  </g>
                  <path
                    d='M32.0394 109.083C40.0817 109.083 46.6013 99.9306 46.6013 88.6405C46.6013 77.3504 40.0817 68.1979 32.0394 68.1979C23.997 68.1979 17.4774 77.3504 17.4774 88.6405C17.4774 99.9306 23.997 109.083 32.0394 109.083Z'
                    fill='#A2C6F0'
                  />
                  <path
                    d='M20.5195 97.5796C24.0362 97.5796 26.887 93.5774 26.887 88.6405C26.887 83.7035 24.0362 79.7014 20.5195 79.7014C17.0029 79.7014 14.1521 83.7035 14.1521 88.6405C14.1521 93.5774 17.0029 97.5796 20.5195 97.5796Z'
                    fill='#001332'
                  />
                  <g opacity='0.4'>
                    <path
                      d='M102.574 154.63C100.135 143.185 73.1507 143.461 72.4166 135.981C71.851 130.214 87.3439 124.505 89.2906 123.787C90.9558 123.173 92.6085 122.654 94.2446 122.21H65.0176C55.2892 122.21 47.4019 130.088 47.4019 139.807C47.4019 158.02 62.1823 172.785 80.4143 172.785H98.1961C99.9044 172.785 101.583 172.656 103.221 172.405C101.674 170.49 100.665 168.394 100.421 166.107C99.8803 161.037 103.52 159.065 102.575 154.631L102.574 154.63Z'
                      fill='#B0DBFA'
                    />
                  </g>
                  <path
                    d='M89.7432 126.586C105.253 126.586 117.826 125.066 117.826 123.19C117.826 121.314 105.253 119.793 89.7432 119.793C74.2337 119.793 61.6608 121.314 61.6608 123.19C61.6608 125.066 74.2337 126.586 89.7432 126.586Z'
                    fill='#A2C6F0'
                  />
                </g>
                <path
                  d='M183.45 120.382C178.656 118.546 173.413 119.471 169.188 122.398C167.644 114.69 162.74 108.166 155.437 105.369C143.47 100.784 129.595 107.94 124.446 121.351C123.12 124.807 122.504 128.34 122.522 131.761C121.905 131.375 121.246 131.044 120.545 130.775C114.006 128.27 106.426 132.18 103.613 139.507C100.801 146.834 103.724 155.071 110.359 157.309C113.902 158.503 119.509 158.526 119.509 158.526C119.509 158.526 174.046 154.469 175.728 154.483C182.446 154.538 189.068 149.948 191.878 142.626C195.393 133.47 191.62 123.512 183.45 120.382V120.382Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M185.072 120.389C180.278 118.552 175.035 119.477 170.81 122.405C169.266 114.697 164.362 108.173 157.058 105.375C145.092 100.791 131.217 107.946 126.068 121.357C124.742 124.813 124.126 128.347 124.144 131.768C123.527 131.381 122.868 131.05 122.167 130.782C115.628 128.277 108.048 132.186 105.235 139.513C102.422 146.84 105.442 154.811 111.981 157.315C113.364 157.845 121.131 158.532 121.131 158.532C121.131 158.532 175.667 154.475 177.35 154.49C184.068 154.545 190.69 149.955 193.5 142.633C197.015 133.477 193.242 123.518 185.072 120.388V120.389Z'
                  fill='#BBD1F6'
                />
                <path
                  d='M55.9478 132.364C53.8607 134.073 53.5551 137.148 55.2659 139.233L69.362 156.414L76.9204 150.226L62.8243 133.045C61.1135 130.96 58.0348 130.655 55.9478 132.364Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M100.132 148.616C99.8272 145.937 97.4063 144.013 94.7246 144.318L72.6292 146.828L73.7337 156.528L95.8292 154.017C98.5108 153.713 100.437 151.295 100.132 148.616Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M77.6427 157.578C80.4115 154.812 80.4115 150.328 77.6427 147.562C74.8739 144.796 70.3847 144.796 67.6159 147.562C64.8471 150.328 64.8471 154.812 67.6159 157.578C70.3847 160.344 74.8738 160.344 77.6427 157.578Z'
                  fill='#001332'
                />
                <path
                  d='M95.6224 145.222L99.2716 154.17C99.8139 155.5 101.871 155.896 103.441 154.952C105.74 153.569 108.254 151.208 107.239 147.795C105.955 143.476 102.136 142.232 98.9934 141.98C96.8606 141.809 94.9754 143.634 95.6224 145.222Z'
                  fill='#001332'
                />
                <path
                  d='M300.111 60.9235L240.778 45.0251C235.443 43.5957 229.96 46.7616 228.531 52.0962L219.226 86.8232C218.998 87.6726 219.502 88.5456 220.351 88.7732C221.362 89.0441 221.842 90.1983 221.32 91.1051C219.635 94.0283 217.606 97.5527 216.112 100.149C215.602 101.035 216.646 101.998 217.488 101.418L227.726 94.37C230.129 92.7156 233.135 92.1932 235.955 92.9398L311.009 112.808C316.337 114.218 321.801 111.052 323.228 105.729L330.343 79.1737C331.781 73.8081 328.57 68.299 323.193 66.9053L300.111 60.9235Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M242.793 74.2642L244.052 65.1808L246.432 65.8185L245.374 70.2019C245.306 70.4543 245.239 70.7067 245.171 70.9592C245.103 71.2116 245.036 71.464 244.968 71.7165C244.9 71.9689 244.833 72.2213 244.765 72.4737L244.819 72.4882C244.923 72.2455 245.031 72.0039 245.144 71.7636C245.266 71.5256 245.387 71.2877 245.509 71.0498C245.633 70.8028 245.75 70.5637 245.861 70.3323L246.92 68.3261L248.272 68.6885L248.146 70.9447C248.128 71.1912 248.108 71.4467 248.086 71.7112C248.072 71.9781 248.057 72.2495 248.041 72.5255C248.028 72.7924 248.004 73.0614 247.97 73.3325L248.024 73.347C248.094 73.0856 248.163 72.8286 248.231 72.5762C248.301 72.3147 248.368 72.0623 248.434 71.8189C248.501 71.5665 248.568 71.3186 248.633 71.0751L249.908 66.7497L252.126 67.344L248.756 75.8621L246.349 75.2172L246.602 72.705C246.634 72.4044 246.668 72.0994 246.702 71.7898C246.746 71.4826 246.798 71.1971 246.859 70.9332L246.819 70.9224C246.731 71.179 246.629 71.4512 246.513 71.739C246.397 72.0269 246.278 72.309 246.156 72.5854L245.146 74.8947L242.793 74.2642ZM254.198 77.4941C253.485 77.3033 252.878 76.9908 252.376 76.5568C251.883 76.1251 251.54 75.6031 251.346 74.9909C251.155 74.3696 251.16 73.6848 251.361 72.9365C251.559 72.1973 251.895 71.6109 252.368 71.1774C252.853 70.7373 253.404 70.45 254.02 70.3154C254.638 70.1719 255.249 70.181 255.853 70.3429C256.565 70.5337 257.12 70.8419 257.518 71.2674C257.919 71.684 258.17 72.1715 258.272 72.7302C258.382 73.2913 258.356 73.8738 258.195 74.4778C258.141 74.6762 258.076 74.8664 257.998 75.0484C257.932 75.2239 257.879 75.3498 257.839 75.4261L252.93 74.1108L253.336 72.5962L256.636 73.4803L256.202 73.7991C256.323 73.3483 256.325 72.9816 256.207 72.6989C256.09 72.4161 255.842 72.2241 255.463 72.1226C255.166 72.0429 254.896 72.0384 254.655 72.109C254.423 72.1821 254.216 72.3394 254.036 72.5808C253.858 72.8133 253.714 73.1324 253.605 73.538C253.492 73.9618 253.468 74.3225 253.533 74.6201C253.607 74.9202 253.763 75.1696 253.999 75.3683C254.247 75.5604 254.579 75.712 254.993 75.8231C255.264 75.8956 255.534 75.9341 255.803 75.9386C256.084 75.9365 256.391 75.9078 256.725 75.8522L257.112 77.4345C256.639 77.5782 256.141 77.6525 255.618 77.6573C255.095 77.662 254.621 77.6077 254.198 77.4941ZM263.131 79.8878C262.23 79.6463 261.621 79.1934 261.307 78.5293C261.003 77.8586 260.969 77.086 261.203 76.2115L262.493 71.3973L260.464 70.8538L260.95 69.0417L265.304 70.2085L263.507 76.9159C263.403 77.3035 263.44 77.5985 263.618 77.8007C263.807 77.9963 264.019 78.1255 264.253 78.1883C264.397 78.227 264.55 78.2533 264.71 78.2673C264.873 78.2724 265.062 78.2699 265.278 78.2599L265.321 80.0979C264.932 80.1094 264.58 80.1069 264.265 80.0903C263.951 80.0737 263.573 80.0062 263.131 79.8878ZM270.577 81.8829C269.865 81.6921 269.258 81.3797 268.756 80.9456C268.262 80.5139 267.919 79.992 267.726 79.3797C267.535 78.7584 267.539 78.0736 267.74 77.3254C267.94 76.5771 268.296 75.991 268.805 75.5672C269.317 75.1343 269.913 74.8591 270.592 74.7415C271.271 74.6238 271.957 74.658 272.651 74.844C273.183 74.9865 273.648 75.1981 274.047 75.4788C274.454 75.7618 274.761 76.076 274.968 76.4214L273.529 77.5722C273.323 77.3332 273.115 77.152 272.907 77.0285C272.71 76.8984 272.503 76.8044 272.287 76.7464C271.953 76.657 271.637 76.6449 271.34 76.7101C271.042 76.7753 270.787 76.9148 270.575 77.1286C270.364 77.3423 270.212 77.6205 270.12 77.9631C270.028 78.3057 270.016 78.6213 270.084 78.91C270.16 79.201 270.302 79.4468 270.509 79.6473C270.725 79.8501 270.991 79.9938 271.307 80.0784C271.64 80.1678 271.958 80.1901 272.26 80.1454C272.571 80.103 272.858 80.04 273.122 79.9561L273.651 81.663C273.144 81.8944 272.618 82.0192 272.072 82.0371C271.526 82.0551 271.028 82.0037 270.577 81.8829ZM278.117 83.9034C277.522 83.744 276.996 83.4533 276.539 83.0313C276.082 82.6093 275.757 82.0922 275.564 81.4799C275.373 80.8586 275.377 80.1738 275.578 79.4256C275.778 78.6773 276.116 78.0864 276.589 77.6529C277.065 77.2104 277.607 76.9206 278.213 76.7837C278.82 76.6467 279.421 76.658 280.016 76.8174C280.611 76.9768 281.137 77.2675 281.594 77.6895C282.051 78.1115 282.375 78.6332 282.566 79.2545C282.76 79.8667 282.756 80.547 282.556 81.2953C282.355 82.0435 282.017 82.6389 281.541 83.0815C281.067 83.515 280.527 83.8002 279.92 83.9371C279.313 84.0741 278.713 84.0629 278.117 83.9034ZM278.618 82.0373C278.879 82.1073 279.109 82.0965 279.307 82.0047C279.506 81.913 279.675 81.7505 279.815 81.5174C279.964 81.2868 280.084 81.0001 280.176 80.6575C280.268 80.315 280.307 80.0066 280.293 79.7324C280.289 79.4607 280.223 79.2354 280.097 79.0567C279.971 78.878 279.778 78.7536 279.516 78.6836C279.255 78.6135 279.025 78.6244 278.826 78.7161C278.628 78.8079 278.454 78.9691 278.305 79.1998C278.166 79.4329 278.05 79.7207 277.958 80.0633C277.866 80.4059 277.823 80.713 277.827 80.9848C277.84 81.259 277.91 81.4854 278.036 81.6641C278.162 81.8428 278.356 81.9672 278.618 82.0373ZM282.564 84.921L284.376 78.1595L286.107 78.6233L286.052 79.4782L286.106 79.4927C286.284 79.2602 286.511 79.0795 286.787 78.9506C287.075 78.8151 287.412 78.7992 287.8 78.9031C288.08 78.978 288.293 79.1367 288.441 79.3791C288.6 79.615 288.668 79.9184 288.646 80.2893C288.823 80.0274 289.062 79.8209 289.363 79.6697C289.664 79.5186 290.009 79.4949 290.397 79.5988C290.883 79.7292 291.203 80.0323 291.356 80.5081C291.511 80.9748 291.498 81.5462 291.317 82.2224L290.056 86.9284L287.865 86.3414L289.083 81.7977C289.155 81.5272 289.115 81.3714 288.961 81.3304C288.871 81.3062 288.779 81.3251 288.685 81.3869C288.592 81.4487 288.489 81.5613 288.378 81.7247L287.189 86.1602L285.431 85.6892L286.649 81.1454C286.687 81.0012 286.692 80.8915 286.664 80.8163C286.636 80.7411 286.577 80.6914 286.487 80.6673C286.406 80.6455 286.323 80.6668 286.238 80.731C286.153 80.7952 286.055 80.9091 285.943 81.0725L284.755 85.508L282.564 84.921ZM294.74 88.3575C294.028 88.1666 293.421 87.8542 292.919 87.4201C292.426 86.9884 292.082 86.4665 291.889 85.8542C291.698 85.2329 291.703 84.5481 291.903 83.7999C292.101 83.0606 292.437 82.4742 292.911 82.0407C293.396 81.6006 293.946 81.3133 294.562 81.1788C295.18 81.0352 295.791 81.0444 296.395 81.2062C297.108 81.397 297.663 81.7052 298.061 82.1308C298.461 82.5473 298.712 83.0349 298.814 83.5935C298.925 84.1546 298.899 84.7371 298.737 85.3411C298.684 85.5395 298.618 85.7297 298.541 85.9118C298.474 86.0872 298.421 86.2131 298.381 86.2894L293.473 84.9741L293.878 83.4595L297.178 84.3437L296.745 84.6624C296.866 84.2116 296.867 83.8449 296.75 83.5622C296.632 83.2795 296.384 83.0874 296.006 82.9859C295.708 82.9062 295.439 82.9017 295.198 82.9723C294.965 83.0454 294.759 83.2027 294.578 83.4442C294.4 83.6766 294.257 83.9957 294.148 84.4014C294.034 84.8251 294.01 85.1858 294.075 85.4835C294.15 85.7836 294.305 86.0329 294.542 86.2316C294.79 86.4237 295.121 86.5753 295.536 86.6864C295.806 86.7589 296.076 86.7974 296.345 86.8019C296.626 86.7999 296.933 86.7711 297.267 86.7156L297.655 88.2978C297.182 88.4415 296.683 88.5158 296.16 88.5206C295.637 88.5254 295.164 88.471 294.74 88.3575ZM302.686 86.4862L303.296 82.9103L303.751 80.8871L306.077 81.5104L305.46 83.49L304.2 86.892L302.686 86.4862ZM302.443 90.4214C302.01 90.3055 301.692 90.0703 301.487 89.716C301.285 89.3526 301.242 88.9545 301.358 88.5218C301.474 88.0891 301.708 87.7749 302.06 87.5793C302.414 87.3747 302.807 87.3304 303.24 87.4464C303.673 87.5623 303.992 87.7975 304.196 88.1518C304.403 88.4972 304.449 88.8862 304.333 89.319C304.217 89.7517 303.98 90.0749 303.624 90.2885C303.269 90.4931 302.876 90.5374 302.443 90.4214Z'
                  fill='var(--bg-secondary)'
                />
                <defs>
                  <filter
                    id='filter0_d_4238_18080'
                    x='4.1521'
                    y='33.2485'
                    width='167.925'
                    height='153.537'
                    filterUnits='userSpaceOnUse'
                    colorInterpolationFilters='sRGB'
                  >
                    <feFlood floodOpacity='0' result='BackgroundImageFix' />
                    <feColorMatrix
                      in='SourceAlpha'
                      type='matrix'
                      values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                      result='hardAlpha'
                    />
                    <feOffset dy='4' />
                    <feGaussianBlur stdDeviation='5' />
                    <feComposite in2='hardAlpha' operator='out' />
                    <feColorMatrix
                      type='matrix'
                      values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0'
                    />
                    <feBlend
                      mode='normal'
                      in2='BackgroundImageFix'
                      result='effect1_dropShadow_4238_18080'
                    />
                    <feBlend
                      mode='normal'
                      in='SourceGraphic'
                      in2='effect1_dropShadow_4238_18080'
                      result='shape'
                    />
                  </filter>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )}
      {step === 2 && (
        <div className='wizard-team-graphic-container'>
          <div className='team-graphic-frame'>
            <div
              className={`team-yes-select${
                teamOpt === 'yes' ? '-selected' : ''
              }`}
              onClick={() => handleTeamYesSelect()}
            >
              <div className='team-yes-frame'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='88'
                  height='80'
                  viewBox='0 0 88 80'
                  fill='none'
                >
                  <g clipPath='url(#clip0_4181_61824)'>
                    <path
                      d='M69.8023 44.4666C68.8901 44.4925 68.0006 44.2665 67.2297 43.8124C64.8819 42.4288 61.4122 40.1437 58.291 37.1522C54.3305 33.3563 52.0546 29.4982 51.557 25.709L51.549 25.6497L51.4525 9.48285C51.4525 6.28778 53.5723 3.69428 57.5519 1.99014C60.8401 0.582354 65.1982 -0.115502 69.8023 0.0156127C74.3526 0.14495 78.5632 1.06808 81.6801 2.60628C85.3847 4.43477 87.3288 7.00411 87.3288 10.0503L87.2406 25.498L87.2334 25.5548C86.7784 29.183 84.6846 32.9335 80.984 36.7259C78.0157 39.7674 74.6617 42.1783 72.3583 43.6717C71.5961 44.166 70.7127 44.4413 69.8027 44.4669L69.8023 44.4666Z'
                      fill='#0765D3'
                    />
                    <path
                      d='M70.7052 44.4666C69.8053 44.4925 68.927 44.2665 68.1666 43.8124C65.8495 42.4288 62.4261 40.1437 59.346 37.1522C55.4376 33.3563 53.1921 29.4982 52.7009 25.709L52.6933 25.6497L52.5979 9.48285C52.5979 6.28778 54.6895 3.69428 58.6167 1.99014C61.8615 0.582354 66.1621 -0.115502 70.7052 0.0156127C75.1955 0.14495 79.3504 1.06808 82.4258 2.60628C86.0816 4.43477 87.9996 7.00411 87.9996 10.0503L87.9125 25.498L87.9057 25.5548C87.4568 29.183 85.3905 32.9335 81.7391 36.7259C78.8101 39.7674 75.5002 42.1783 73.2272 43.6717C72.4751 44.166 71.6033 44.4413 70.7052 44.4669V44.4666Z'
                      fill='#4895EF'
                    />
                    <path
                      d='M53.684 9.49458L53.7801 25.5797C54.8571 33.787 64.668 40.4315 68.9407 42.9561C70.1648 43.6792 71.7298 43.6365 72.9453 42.8502C77.1291 40.1437 86.4025 33.33 87.3932 25.4394L87.481 10.0343C87.481 -1.39502 53.684 -2.43967 53.684 9.49458Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M70.9476 40.4767C70.8692 40.4784 70.7507 40.4656 70.631 40.3942C68.8716 39.3467 65.4612 37.153 62.4943 34.2876C59.2896 31.1928 57.4413 28.1839 57.0173 25.3576L56.9233 9.539C56.9273 8.02106 58.1493 6.72235 60.5448 5.68268C63.219 4.52219 67.0172 3.91352 70.9476 4.0052C74.8391 4.09616 78.526 4.86615 81.0805 6.11226C83.3401 7.21448 84.4822 8.5139 84.4858 9.97925L84.3984 25.2503C84.0026 27.9849 82.2681 30.9334 79.2221 34.0325C76.3581 36.9465 73.0128 39.2565 71.2642 40.3797C71.1446 40.4564 71.0261 40.4749 70.948 40.4767H70.9476Z'
                      fill='#97B5D9'
                    />
                    <path
                      d='M70.5461 39.3954C68.7693 38.3195 65.6098 36.2422 62.8532 33.5752C59.8415 30.6612 58.0987 27.8648 57.6888 25.2777L57.5956 9.5507C57.5985 8.46554 58.613 7.46992 60.6037 6.60364C63.1369 5.50072 66.7667 4.91977 70.5461 5.00327C74.289 5.08606 77.8168 5.81269 80.2413 6.99201C82.125 7.90839 83.0781 8.90792 83.0806 9.96607L82.9931 25.181C82.6086 27.6914 80.9648 30.437 78.0901 33.3567C75.421 36.0674 72.3149 38.247 70.5461 39.3957V39.3954Z'
                      fill='#DEE6F2'
                    />
                    <path
                      d='M78.5 26.75V25.25C78.5 23.8521 77.5439 22.6775 76.25 22.3445M73.625 13.4681C74.7244 13.9131 75.5 14.991 75.5 16.25C75.5 17.509 74.7244 18.5869 73.625 19.0319M74.75 26.75C74.75 25.3522 74.75 24.6533 74.5216 24.1019C74.2172 23.3669 73.6331 22.7828 72.8981 22.4784C72.3467 22.25 71.6478 22.25 70.25 22.25H68C66.6022 22.25 65.9033 22.25 65.3519 22.4784C64.6169 22.7828 64.0328 23.3669 63.7284 24.1019C63.5 24.6533 63.5 25.3522 63.5 26.75M72.125 16.25C72.125 17.9069 70.7819 19.25 69.125 19.25C67.4681 19.25 66.125 17.9069 66.125 16.25C66.125 14.5931 67.4681 13.25 69.125 13.25C70.7819 13.25 72.125 14.5931 72.125 16.25Z'
                      stroke='#4895EF'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M15.4002 74.1091C15.4002 74.1091 12.9169 75.5336 13.6578 77.211C14.1034 78.2198 15.1902 78.5737 15.9861 78.6952C16.4863 78.7716 16.9482 78.4156 16.9995 77.9206L17.143 76.5334L15.4006 74.1091H15.4002Z'
                      fill='url(#paint0_linear_4181_61824)'
                    />
                    <path
                      d='M45.2147 60.0454C45.0647 61.4134 46.0742 65.3546 47.4653 65.5017L58.9271 64.0026L59.4696 59.0494L48.0049 57.8356C46.6138 57.6881 45.3644 58.6777 45.2144 60.0454H45.2147Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M67.306 50.619C66.2185 49.7534 64.6232 49.9183 63.7424 50.9875L56.4865 59.7981L60.4249 62.9331L67.6808 54.1225C68.5612 53.0533 68.3935 51.4849 67.306 50.619Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M59.4861 64.8596C61.4113 64.2255 62.4492 62.1771 61.8043 60.2843C61.1594 58.3916 59.0758 57.3712 57.1506 58.0052C55.2253 58.6393 54.1874 60.6877 54.8323 62.5804C55.4772 64.4732 57.5608 65.4936 59.4861 64.8596Z'
                      fill='#001332'
                    />
                    <path
                      d='M53.1483 49.6628C56.287 49.6628 58.8314 46.1475 58.8314 41.8112C58.8314 37.4749 56.287 33.9597 53.1483 33.9597C50.0097 33.9597 47.4653 37.4749 47.4653 41.8112C47.4653 46.1475 50.0097 49.6628 53.1483 49.6628Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M35.7794 22.7858H25.0835C14.1161 22.7858 5.22546 31.5264 5.22546 42.3087C5.22546 48.0625 9.9695 52.7264 15.822 52.7264H45.0413C50.8934 52.7264 55.6378 48.0625 55.6378 42.3087C55.6378 31.5264 46.7472 22.7858 35.7798 22.7858H35.7794Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M34.993 80H26.1277C17.0375 80 9.66809 72.7553 9.66809 63.8182C9.66809 59.0495 13.6004 55.1835 18.451 55.1835H42.6696C47.5203 55.1835 51.4526 59.0495 51.4526 63.8182C51.4526 72.7553 44.0835 80 34.993 80Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M45.4883 48.5155H25.1565C22.401 48.5155 20.1406 46.439 20.1406 43.8778V39.9504C20.1406 37.3893 22.4013 35.3838 25.1565 35.4702L45.4883 36.108C47.9489 36.1851 49.9215 38.1763 49.9215 40.5566V44.2065C49.9215 46.5864 47.9489 48.5162 45.4883 48.5162V48.5155Z'
                      fill='#4895EF'
                    />
                    <path
                      d='M31.6708 41.7057C31.6708 43.4163 30.1896 44.7846 28.3478 44.7615C26.4894 44.7384 24.9703 43.3104 24.9703 41.5718C24.9703 39.8332 26.4894 38.4659 28.3478 38.5171C30.1896 38.5679 31.6708 39.9952 31.6708 41.7057Z'
                      fill='white'
                    />
                    <path
                      d='M45.4107 41.9804C45.4107 43.6334 44.0275 44.9562 42.3079 44.9349C40.5734 44.9132 39.1559 43.5346 39.1559 41.8553C39.1559 40.1761 40.573 38.8539 42.3079 38.9015C44.0275 38.9488 45.4107 40.3271 45.4107 41.98V41.9804Z'
                      fill='white'
                    />
                    <g opacity='0.3'>
                      <path
                        d='M37.8099 49.9883C34.7863 46.4955 36.1297 43.702 32.1768 38.9687C26.7374 32.456 20.3922 33.1929 18.9386 28.7755C18.536 27.5514 18.4897 25.8715 19.57 23.5491C11.2854 25.8985 5.22583 33.4075 5.22583 42.3087C5.22583 48.0625 9.96987 52.7265 15.8224 52.7265H41.2868C39.7616 51.9092 38.647 50.9541 37.8106 49.988L37.8099 49.9883Z'
                        fill='white'
                      />
                    </g>
                    <path
                      d='M8.08292 49.6081C11.2216 49.6081 13.7659 46.0929 13.7659 41.7566C13.7659 37.4202 11.2216 33.905 8.08292 33.905C4.94427 33.905 2.3999 37.4202 2.3999 41.7566C2.3999 46.0929 4.94427 49.6081 8.08292 49.6081Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M3.58744 45.19C4.95995 45.19 6.07258 43.6528 6.07258 41.7565C6.07258 39.8603 4.95995 38.3231 3.58744 38.3231C2.21493 38.3231 1.10229 39.8603 1.10229 41.7565C1.10229 43.6528 2.21493 45.19 3.58744 45.19Z'
                      fill='#001332'
                    />
                    <g opacity='0.4'>
                      <path
                        d='M37.1764 71.0917C35.9602 65.4758 22.5062 65.6116 22.1404 61.9407C21.8585 59.1109 29.5829 56.3095 30.5536 55.9574C31.3838 55.6565 32.2079 55.4017 33.0236 55.1835H18.4514C13.6007 55.1835 9.66846 59.0495 9.66846 63.8182C9.66846 72.7553 17.0375 80 26.1277 80H34.993C35.8449 80 36.6816 79.9364 37.4984 79.8138C36.7268 78.8737 36.224 77.8454 36.1022 76.7232C35.8326 74.2356 37.6477 73.2681 37.1764 71.0917Z'
                        fill='#D1E3F4'
                      />
                    </g>
                    <path
                      d='M30.7787 57.3311C38.5116 57.3311 44.7803 56.585 44.7803 55.6646C44.7803 54.7443 38.5116 53.9982 30.7787 53.9982C23.0459 53.9982 16.7772 54.7443 16.7772 55.6646C16.7772 56.585 23.0459 57.3311 30.7787 57.3311Z'
                      fill='#A2C6F0'
                    />
                    <path
                      d='M2.80579 64.245L11.4756 59.0509C12.6961 58.3196 14.2835 58.6316 15.1245 59.7679C16.0816 61.0616 15.6909 62.8851 14.2842 63.6889L5.48935 68.7149L2.80579 64.2446V64.245Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M19.9238 70.9442L20.2928 73.0961C20.4048 73.7502 19.858 74.3265 19.1858 74.2722C17.7614 74.1574 15.6037 74.3091 14.7569 76.0744C14.5342 76.5388 14.4887 77.0668 14.6134 77.5653L14.8165 78.3765C14.8165 78.3765 10.8419 77.0892 12.7203 72.6626C14.1952 69.1864 17.7321 69.739 19.1829 70.1366C19.5685 70.2421 19.8569 70.5558 19.9234 70.9439L19.9238 70.9442Z'
                      fill='#001332'
                    />
                    <path
                      d='M4.58283 65.5405L13.2263 70.7769C14.4432 71.5142 14.8812 73.046 14.2328 74.2985C13.4948 75.724 11.6649 76.2098 10.2977 75.3424L1.74963 69.9205L4.58283 65.5405Z'
                      fill='var(--text-primary)'
                    />
                    <path
                      d='M3.67639 70.9144C5.70681 70.9144 7.35279 69.2962 7.35279 67.3C7.35279 65.3039 5.70681 63.6857 3.67639 63.6857C1.64598 63.6857 0 65.3039 0 67.3C0 69.2962 1.64598 70.9144 3.67639 70.9144Z'
                      fill='#001332'
                    />
                    <path
                      d='M66.4593 46.4273L63.9785 44.6716C63.9785 44.6716 63.1396 47.1937 63.4175 47.8763C63.6955 48.5589 66.3342 50.8802 66.3342 50.8802C66.3342 50.8802 66.1166 48.9199 66.7404 47.3963C66.8836 47.047 66.7701 46.6469 66.4593 46.4269V46.4273Z'
                      fill='#00326C'
                    />
                    <path
                      d='M63.9784 44.672C64.2065 44.672 64.3695 44.8902 64.2986 45.1033C63.926 46.2269 63.2447 49.2024 66.1954 49.8565C68.9458 50.4666 70.1009 48.9661 71.0616 46.6494C71.2011 46.3125 71.6015 46.1611 71.9355 46.3175L74.0794 47.3209C74.4373 47.4883 74.5601 47.9314 74.3353 48.2519C73.3302 49.6835 70.5097 52.9645 66.5427 51.6317C61.6985 50.004 63.2433 45.8247 63.6712 44.8685C63.725 44.7484 63.8454 44.6716 63.9788 44.6716L63.9784 44.672Z'
                      fill='#001332'
                    />
                  </g>
                  <defs>
                    <linearGradient
                      id='paint0_linear_4181_61824'
                      x1='13.5197'
                      y1='76.4077'
                      x2='17.1426'
                      y2='76.4077'
                      gradientUnits='userSpaceOnUse'
                    >
                      <stop stopColor='#444B8C' />
                      <stop offset='1' stopColor='#26264F' />
                    </linearGradient>
                    <clipPath id='clip0_4181_61824'>
                      <rect width='88' height='80' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
              </div>
              <div className='wizard-team-option-text'>Yes</div>
            </div>
            <div
              className={`team-no-select${teamOpt === 'no' ? '-selected' : ''}`}
              onClick={() => handleTeamNoSelect()}
            >
              <div className='team-no-frame'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='126'
                  height='82'
                  viewBox='0 0 126 82'
                  fill='none'
                >
                  <path
                    d='M34.4205 66.03C49.2089 64.5644 60.2943 54.0996 59.1803 42.6564C58.0662 31.2132 45.1747 23.1248 30.3863 24.5905C15.5978 26.0561 4.51246 36.5209 5.62648 47.9641C6.7405 59.4073 19.632 67.4957 34.4205 66.03Z'
                    fill='var(--text-primary)'
                  />
                  <path
                    d='M14.2384 41.5443L50.0769 37.9924C50.723 37.9283 51.2996 38.4055 51.3631 39.058L52.2967 48.6477C52.3966 49.6742 51.652 50.5893 50.6346 50.6901L16.1401 54.1089C15.1227 54.2097 14.2158 53.4583 14.1159 52.4318L13.1823 42.8421C13.1188 42.1902 13.5918 41.6084 14.2384 41.5443Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M27.501 47.3475C27.7106 46.9721 27.8236 46.5618 27.7821 46.1355C27.6481 44.7544 26.0885 43.7791 24.3001 43.9562C22.511 44.1333 21.1701 45.3963 21.3042 46.7773C21.3456 47.2037 21.5353 47.5842 21.813 47.9114L27.501 47.3481V47.3475Z'
                    fill='white'
                  />
                  <path
                    d='M43.8147 45.7321C44.0242 45.3567 44.1373 44.9464 44.0958 44.5201C43.9618 43.139 42.4022 42.1637 40.6132 42.3408C38.8241 42.5178 37.4832 43.7808 37.6173 45.1619C37.6587 45.5882 37.8484 45.9687 38.1261 46.296L43.8141 45.7327L43.8147 45.7321Z'
                    fill='white'
                  />
                  <path
                    d='M35.1484 65.6782L34.9905 64.053C48.6711 62.6984 58.9276 51.2362 58.8475 37.3913L60.4656 37.3815C60.5059 44.402 57.9587 51.16 53.2919 56.4097C48.5961 61.6926 42.1522 64.9842 35.1478 65.6776L35.1484 65.6782Z'
                    fill='#001332'
                  />
                  <path
                    d='M6.51039 46.5762L4.44194 46.7762C4.27781 45.8919 4.15172 45.0072 4.06824 44.1447L4.51748 44.1C5.01784 37.9248 7.506 32.0717 11.6565 27.4007C16.3903 22.0737 22.8853 18.7551 29.9449 18.056C37.0078 17.3569 44.0208 19.3379 49.6917 23.6341C54.6635 27.4007 58.2324 32.6545 59.913 38.6142L60.3622 38.5695C60.4463 39.4314 60.4934 40.3248 60.5025 41.2239L58.4346 41.4336C55.8681 27.9577 43.7078 18.8084 30.1482 20.1511C16.5937 21.4932 6.43145 32.8533 6.51039 46.5756V46.5762Z'
                    fill='#001332'
                  />
                  <path
                    d='M9.42167 55.4493L3.26516 56.059C2.5558 56.1295 1.92368 55.6057 1.85439 54.89L0.00630016 35.8899C-0.0635569 35.1741 0.455543 34.5363 1.1649 34.4664L7.32141 33.8567C8.03077 33.7862 8.66289 34.31 8.73218 35.0257L10.5803 54.0258C10.6501 54.7415 10.131 55.3793 9.42167 55.4493Z'
                    fill='#001332'
                  />
                  <path
                    d='M34.5475 66.6828L33.6939 63.2325C33.5956 62.8348 33.8353 62.4319 34.2295 62.3328L44.6881 59.699C45.0823 59.5999 45.4816 59.8417 45.5798 60.2394L46.4334 63.6898C46.5317 64.0875 46.292 64.4903 45.8979 64.5895L35.4392 67.2232C35.045 67.3223 34.6458 67.0805 34.5475 66.6828Z'
                    fill='#001332'
                  />
                  <path
                    d='M64.11 50.034L57.9535 50.6437C57.2442 50.7142 56.612 50.1904 56.5427 49.4747L54.6947 30.4746C54.6248 29.7589 55.1439 29.1211 55.8533 29.0511L62.0098 28.4414C62.7191 28.3709 63.3512 28.8947 63.4205 29.6104L65.2686 48.6105C65.3385 49.3263 64.8194 49.9641 64.11 50.034Z'
                    fill='#001332'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M48.7688 48.713C39.341 49.0242 28.7585 45.9229 25.3758 36.5071C24.1127 32.7192 24.6721 28.7514 26.4066 25.2208C13.6614 27.9921 4.61235 37.5747 5.62328 47.9652C6.73645 59.4085 19.6271 67.4977 34.4157 66.0335C45.1618 64.9693 53.9519 59.1529 57.5276 51.662C55.9425 51.7273 54.3142 51.4752 52.6746 50.8156C51.3927 50.3199 50.2148 48.7772 48.7688 48.713Z'
                      fill='#D8DEE8'
                    />
                  </g>
                  <path
                    d='M74.1882 33.0848C71.6205 29.8402 70.1416 26.0306 70.1416 21.955C70.1416 10.1061 82.646 0.5 98.0708 0.5C113.496 0.5 126 10.1061 126 21.9556C126 33.8051 113.496 43.4112 98.0708 43.4112C92.2897 43.4112 86.9192 42.0617 82.4631 39.7505C79.5178 40.2577 74.9458 41.1093 72.5332 41.5625C71.8227 41.6961 71.2406 40.9952 71.495 40.3127L74.1882 33.0854V33.0848Z'
                    fill='#4895EF'
                  />
                  <path
                    d='M97 25H94C92.1362 25 91.2044 25 90.4693 25.3045C89.4892 25.7105 88.7105 26.4892 88.3045 27.4693C88 28.2044 88 29.1362 88 31M101.5 13.2908C102.966 13.8841 104 15.3213 104 17M97.9999 31.5L100.025 31.095C100.202 31.0597 100.29 31.042 100.372 31.0097C100.445 30.9811 100.515 30.9439 100.579 30.899C100.652 30.8484 100.715 30.7848 100.843 30.6574L107.5 24C108.052 23.4477 108.052 22.5523 107.5 22C106.948 21.4477 106.052 21.4477 105.5 22L98.8425 28.6575C98.7152 28.7848 98.6516 28.8484 98.601 28.921C98.5561 28.9853 98.5189 29.0548 98.4902 29.1278C98.458 29.2102 98.4403 29.2984 98.405 29.475L97.9999 31.5ZM99.5 17C99.5 19.2091 97.7091 21 95.5 21C93.2909 21 91.5 19.2091 91.5 17C91.5 14.7909 93.2909 13 95.5 13C97.7091 13 99.5 14.7909 99.5 17Z'
                    stroke='var(--text-primary)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M82.7869 26.4959C89.0496 23.8398 95.9365 29.446 100.242 25.5355C104.582 21.5929 99.8928 13.7988 104.05 8.24365C106.032 5.595 109.362 4.34231 113.171 3.90393C120.888 7.7216 125.999 14.3799 125.999 21.955C125.999 33.8046 113.494 43.4106 98.0697 43.4106C92.2886 43.4106 86.9181 42.0611 82.462 39.75C80.138 40.15 76.8025 40.7649 74.3126 41.2285C76.1494 31.6213 79.5269 27.8775 82.7863 26.4953L82.7869 26.4959Z'
                      fill='#4895EF'
                    />
                  </g>
                  <path
                    d='M84.3863 74.6556C85.6749 72.7399 86.3337 70.5577 86.1594 68.2925C85.6522 61.7053 78.2894 56.91 69.7146 57.5821C61.1398 58.2543 54.5999 64.1396 55.1071 70.7268C55.6143 77.314 62.9771 82.1093 71.5519 81.4371C74.7659 81.185 77.6936 80.201 80.0716 78.722C81.7306 78.8756 84.3085 79.1495 85.6693 79.2968C86.0697 79.3398 86.3633 78.9249 86.1929 78.5564L84.3863 74.6562V74.6556Z'
                    fill='#A2C6F0'
                  />
                  <path
                    d='M70.5 67V72M68 69.5H73M67.875 75.125H73.125C74.1751 75.125 74.7001 75.125 75.1012 74.9206C75.454 74.7409 75.7409 74.454 75.9206 74.1012C76.125 73.7001 76.125 73.1751 76.125 72.125V66.875C76.125 65.8249 76.125 65.2999 75.9206 64.8988C75.7409 64.546 75.454 64.2591 75.1012 64.0794C74.7001 63.875 74.1751 63.875 73.125 63.875H67.875C66.8249 63.875 66.2999 63.875 65.8988 64.0794C65.546 64.2591 65.2591 64.546 65.0794 64.8988C64.875 65.2999 64.875 65.8249 64.875 66.875V72.125C64.875 73.1751 64.875 73.7001 65.0794 74.1012C65.2591 74.454 65.546 74.7409 65.8988 74.9206C66.2999 75.125 66.8249 75.125 67.875 75.125Z'
                    stroke='var(--text-primary)'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <g opacity='0.3'>
                    <path
                      d='M79.3242 71.3675C75.7292 70.1641 72.1409 73.5806 69.58 71.5944C66.9987 69.5922 69.2711 65.0547 66.7227 62.1477C65.5073 60.7615 63.603 60.2102 61.4664 60.1328C57.3397 62.5912 54.7834 66.5155 55.1077 70.7268C55.6149 77.314 62.9777 82.1093 71.5525 81.4371C74.7665 81.185 77.6942 80.2011 80.0722 78.722C81.3813 78.8429 83.2618 79.0395 84.6658 79.189C83.2334 73.9284 81.1956 71.9944 79.3242 71.3681V71.3675Z'
                      fill='#A2C6F0'
                    />
                  </g>
                </svg>
              </div>
              <div className='wizard-team-option-text'>No</div>
            </div>
          </div>
        </div>
      )}
      {step === 3 && teamOpt === 'yes' && (
        <div className='check-email-wrap'>
          <div className='check-email-frame'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='254'
              height='161'
              viewBox='0 0 254 161'
              fill='none'
            >
              <path
                d='M202.179 3.49754L21.2318 0.048789C17.4109 -0.0238245 14.2417 3.09543 14.2417 6.92911V123.868C14.2417 127.809 17.5845 130.868 21.5043 130.512L202.451 114.089C205.977 113.769 208.703 110.78 208.703 107.235V10.1678C208.703 6.51685 205.818 3.56703 202.179 3.49754Z'
                fill='#BEDBF7'
              />
              <path
                d='M205.288 3.44993L24.3416 0.00118158C20.5207 -0.0714319 17.3516 3.04782 17.3516 6.8815V123.82C17.3516 127.762 20.6944 130.82 24.6142 130.464L205.561 114.041C209.087 113.721 211.813 110.733 211.813 107.187V10.1202C211.813 6.46925 208.928 3.51942 205.288 3.44993Z'
                fill='url(#paint0_linear_4238_41733)'
              />
              <path
                d='M134.248 69.5813L210.068 5.61272C208.882 4.31349 207.191 3.48585 205.289 3.44915L24.3417 0.00118425C22.62 -0.0316089 21.0316 0.585215 19.8052 1.6221L121.165 70.3441C125.16 73.0519 130.503 72.7411 134.248 69.5813Z'
                fill='url(#paint1_linear_4238_41733)'
              />
              <path
                d='M134.248 69.5813C130.503 72.7404 125.16 73.0519 121.165 70.3441L19.8051 1.62134C18.3093 2.88544 17.3516 4.77417 17.3516 6.88074V123.82C17.3516 127.761 20.6944 130.819 24.6142 130.463L205.561 114.04C209.087 113.72 211.813 110.732 211.813 107.186V10.1202C211.813 8.37751 211.15 6.79954 210.068 5.61352L134.248 69.5813Z'
                fill='#DFE8F3'
              />
              <path
                d='M134.248 69.5813L210.068 5.61272C208.882 4.31349 207.191 3.48585 205.289 3.44915L24.3417 0.00118425C22.62 -0.0316089 21.0316 0.585215 19.8052 1.6221L121.165 70.3441C125.16 73.0519 130.503 72.7411 134.248 69.5813Z'
                fill='#EAEDEF'
              />
              <g opacity='0.37'>
                <path
                  d='M210.068 5.61274L134.248 69.5805C130.503 72.7396 125.16 73.0511 121.165 70.3433L19.8051 1.62134C18.3093 2.88544 17.3516 4.77417 17.3516 6.88074V24.1268C21.0136 22.527 24.1127 22.1827 26.6317 22.3544C33.5953 22.8299 35.2577 27.1625 48.5924 33.248C61.0596 38.9376 62.3677 36.4102 67.8635 40.5882C78.4518 48.6382 73.8211 58.1904 83.1012 66.8657C92.7644 75.8995 101.102 68.6412 114.921 77.1573C129.524 86.1559 124.849 97.1174 136.881 103.33C153.739 112.035 170.954 94.6704 185.284 103.477C189.616 106.139 192.387 110.399 194.172 115.074L205.562 114.04C209.088 113.72 211.814 110.732 211.814 107.186V10.1202C211.814 8.37751 211.151 6.79954 210.069 5.61352L210.068 5.61274Z'
                  fill='#DFE8F3'
                />
              </g>
              <path
                d='M168.984 147.181L1.77832 145.72V150.041C1.77832 151.214 2.50793 152.211 3.49528 152.389L50.7758 160.897C51.3372 160.998 51.9056 161.025 52.4725 160.977L167.167 151.276C168.191 151.189 168.984 150.189 168.984 148.985V147.181Z'
                fill='#A2C6F0'
              />
              <path
                d='M115.158 140.176L3.61845 148.08C1.62274 148.222 0 146.719 0 144.723V64.7685C0 62.7728 1.62274 61.155 3.61845 61.155H115.158C116.803 61.155 118.132 62.6175 118.132 64.4219V136.7C118.132 138.504 116.803 140.06 115.158 140.177V140.176Z'
                fill='#A2C6F0'
              />
              <path
                d='M116.937 140.176L5.39677 148.08C3.40106 148.222 1.77832 146.719 1.77832 144.723V64.7685C1.77832 62.7728 3.40106 61.155 5.39677 61.155H116.937C118.581 61.155 119.91 62.6175 119.91 64.4219V136.7C119.91 138.504 118.581 140.06 116.937 140.177V140.176Z'
                fill='#BEDBF7'
              />
              <path
                d='M113.462 135.081L9.5745 141.947C7.95021 142.053 6.63037 140.821 6.63037 139.191V69.1901C6.63037 67.5606 7.95021 66.2349 9.5745 66.2278L113.462 65.7968C114.819 65.7914 115.916 66.9899 115.916 68.4734V132.232C115.916 133.715 114.819 134.991 113.462 135.081Z'
                fill='#4895EF'
              />
              <path
                d='M65.1064 63.342C65.1064 64.0846 64.5325 64.6889 63.8232 64.6912C63.113 64.6936 62.5376 64.0916 62.5376 63.3467C62.5376 62.6018 63.1138 61.9983 63.8232 61.9975C64.5318 61.9975 65.1064 62.5987 65.1064 63.342Z'
                fill='white'
              />
              <g opacity='0.48'>
                <path
                  d='M90.0633 118.248C81.4645 113.608 74.7034 119.487 68.1782 113.687C64.2225 110.171 66.1645 107.549 61.2971 102.294C55.9056 96.4741 52.3962 98.4307 46.9775 93.1924C40.1268 86.5689 43.5529 81.4165 37.6522 76.6607C30.4931 70.8923 22.7727 76.2313 15.9578 70.6986C13.0183 68.3125 11.4057 64.8435 10.564 61.1542H3.89551C2.72673 61.1542 1.77832 62.1037 1.77832 63.2756V146.157C1.77832 147.328 2.72751 148.21 3.89551 148.127L117.573 140.036C118.13 139.996 118.626 139.67 118.942 139.196C110.621 138.892 105.509 136.094 102.241 133.202C95.848 127.546 96.9568 121.967 90.0625 118.247L90.0633 118.248Z'
                  fill='#EAEDEF'
                />
              </g>
              <path
                d='M77.9168 115.375V112.292C77.9168 109.418 75.9516 107.004 73.2918 106.319M67.896 88.0733C70.1559 88.9881 71.7502 91.2037 71.7502 93.7917C71.7502 96.3796 70.1559 98.5953 67.896 99.5101M70.2085 115.375C70.2085 112.502 70.2085 111.065 69.7391 109.932C69.1132 108.421 67.9127 107.22 66.4017 106.594C65.2685 106.125 63.8318 106.125 60.9585 106.125H56.3335C53.4602 106.125 52.0235 106.125 50.8903 106.594C49.3793 107.22 48.1788 108.421 47.5529 109.932C47.0835 111.065 47.0835 112.502 47.0835 115.375M64.8127 93.7917C64.8127 97.1974 62.0518 99.9583 58.646 99.9583C55.2402 99.9583 52.4793 97.1974 52.4793 93.7917C52.4793 90.3859 55.2402 87.625 58.646 87.625C62.0518 87.625 64.8127 90.3859 64.8127 93.7917Z'
                stroke='#EAEDEF'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
              <path
                d='M114.154 139.818L1.77832 148.138L50.7758 156.955C51.3372 157.056 51.9056 157.083 52.4725 157.034L168.984 147.18L115.941 139.874C115.348 139.792 114.75 139.774 114.154 139.818Z'
                fill='#D1E8FA'
              />
              <path
                d='M18.1758 148.347L111.732 141.318L140.686 145.273L47.9363 152.788L18.1758 148.347Z'
                fill='#EAEDEF'
              />
              <path
                d='M176.409 57.7203C180.509 57.7203 183.834 54.5546 183.834 50.6495C183.834 46.7444 180.509 43.5787 176.409 43.5787C172.308 43.5787 168.984 46.7444 168.984 50.6495C168.984 54.5546 172.308 57.7203 176.409 57.7203Z'
                fill='white'
              />
              <g filter='url(#filter0_d_4238_41733)'>
                <path
                  d='M205.36 101.995L201.445 107.98L221.011 120.78L224.926 114.794L205.36 101.995Z'
                  fill='white'
                />
                <path
                  d='M224.265 117.088C225.035 113.824 223.014 110.553 219.75 109.783C216.486 109.013 213.216 111.034 212.446 114.298C211.675 117.562 213.697 120.832 216.96 121.602C220.224 122.373 223.494 120.351 224.265 117.088Z'
                  fill='url(#paint2_linear_4238_41733)'
                />
                <path
                  d='M218.064 86.551C223.209 86.551 227.379 80.6901 227.379 73.4603C227.379 66.2305 223.209 60.3696 218.064 60.3696C212.919 60.3696 208.749 66.2305 208.749 73.4603C208.749 80.6901 212.919 86.551 218.064 86.551Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M189.594 41.7404H172.062C154.086 41.7404 139.513 56.3138 139.513 74.2906C139.513 83.8837 147.29 91.6596 156.882 91.6596H204.774C214.366 91.6596 222.143 83.883 222.143 74.2906C222.143 56.3138 207.57 41.7404 189.594 41.7404H189.594Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M186.516 127.252H175.142C163.479 127.252 154.025 117.797 154.025 106.135C154.025 99.9108 159.07 94.866 165.294 94.866H196.365C202.588 94.866 207.633 99.9108 207.633 106.135C207.633 117.798 198.178 127.252 186.516 127.252H186.516Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M205.508 84.6388H172.182C167.666 84.6388 163.961 81.1766 163.961 76.9068V70.3587C163.961 66.0882 167.666 62.745 172.182 62.8888L205.508 63.9522C209.541 64.0811 212.774 67.4009 212.774 71.3689V77.4537C212.774 81.4217 209.541 84.6388 205.508 84.6388Z'
                  fill='#4895EF'
                />
                <path
                  d='M182.86 73.2845C182.86 76.1367 180.432 78.4179 177.414 78.3796C174.367 78.3413 171.878 75.9603 171.878 73.0613C171.878 70.1623 174.367 67.8826 177.414 67.9683C180.433 68.0526 182.86 70.433 182.86 73.2845Z'
                  fill='white'
                />
                <path
                  d='M205.38 73.742C205.38 76.4979 203.113 78.704 200.294 78.6686C197.452 78.6324 195.128 76.3335 195.128 73.5338C195.128 70.734 197.451 68.5293 200.294 68.6094C203.113 68.6887 205.38 70.9862 205.38 73.742Z'
                  fill='white'
                />
                <g opacity='0.3'>
                  <path
                    d='M192.923 87.0937C187.967 81.2702 190.169 76.6129 183.689 68.7208C174.774 57.8625 164.373 59.0909 161.991 51.7259C161.331 49.6849 161.255 46.8844 163.026 43.012C149.447 46.929 139.514 59.4487 139.514 74.2899C139.514 83.883 147.29 91.6589 156.883 91.6589H198.621C196.121 90.2958 194.293 88.704 192.923 87.093V87.0937Z'
                    fill='#F3F4F8'
                  />
                </g>
                <path
                  d='M144.199 86.4603C149.343 86.4603 153.514 80.5994 153.514 73.3696C153.514 66.1398 149.343 60.2789 144.199 60.2789C139.054 60.2789 134.884 66.1398 134.884 73.3696C134.884 80.5994 139.054 86.4603 144.199 86.4603Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M136.829 79.0938C139.079 79.0938 140.903 76.531 140.903 73.3696C140.903 70.2082 139.079 67.6453 136.829 67.6453C134.58 67.6453 132.756 70.2082 132.756 73.3696C132.756 76.531 134.58 79.0938 136.829 79.0938Z'
                  fill='#001332'
                />
                <path
                  opacity='0.4'
                  d='M189.317 115.626C187.756 108.297 170.495 108.475 170.026 103.684C169.664 99.9909 179.574 96.3353 180.819 95.8755C181.884 95.4824 182.941 95.1501 183.988 94.8653H165.293C159.069 94.8653 154.024 99.9101 154.024 106.134C154.024 117.797 163.479 127.252 175.141 127.252H186.515C187.609 127.252 188.682 127.169 189.73 127.009C188.74 125.782 188.094 124.44 187.939 122.976C187.593 119.729 189.922 118.466 189.317 115.626Z'
                  fill='#D1E3F4'
                />
                <path
                  d='M181.109 97.6686C191.03 97.6686 199.072 96.6948 199.072 95.4937C199.072 94.2925 191.03 93.3187 181.109 93.3187C171.188 93.3187 163.146 94.2925 163.146 95.4937C163.146 96.6948 171.188 97.6686 181.109 97.6686Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M177.619 114.793L161.046 103.951C159.393 102.87 157.176 103.333 156.095 104.986C155.014 106.639 155.478 108.856 157.13 109.937L173.703 120.778L177.619 114.793Z'
                  fill='white'
                />
                <path
                  d='M174.501 113.678H191.629L190.949 120.765L174.161 120.203L174.501 113.678Z'
                  fill='white'
                />
                <path
                  d='M175.351 119.992C177.722 117.621 177.722 113.776 175.351 111.405C172.979 109.033 169.135 109.033 166.764 111.405C164.392 113.776 164.392 117.621 166.764 119.992C169.135 122.363 172.979 122.363 175.351 119.992Z'
                  fill='#001332'
                />
                <path
                  d='M193.122 113.482C192.084 112.901 190.618 112.491 188.556 112.567C187.576 112.603 186.767 113.347 186.652 114.321L186.055 119.388H194.891L195.179 118.177C195.619 116.329 194.779 114.411 193.122 113.482V113.482Z'
                  fill='#001332'
                />
                <path
                  d='M208.723 119.508L176.85 118.957C176.411 118.949 175.997 119.158 175.741 119.514L174.769 120.867C174.285 121.541 174.751 122.482 175.58 122.504L209.318 123.443L208.724 119.508H208.723Z'
                  fill='#BEDBF7'
                />
                <path
                  d='M242.936 103.996L210.07 102.465C209.098 102.42 208.174 102.89 207.638 103.703L194.951 122.952L229.708 123.858C230.663 123.883 231.564 123.425 232.108 122.641L243.8 105.741C244.298 105.021 243.81 104.036 242.935 103.995L242.936 103.996Z'
                  fill='#BEDBF7'
                />
                <g opacity='0.3'>
                  <path
                    d='M207.638 103.703C208.173 102.89 209.098 102.419 210.07 102.465L210.791 102.498C210.828 103.565 211.089 104.555 211.686 105.381C213.549 107.957 217.011 106.672 219.346 109.423C221.912 112.445 219.146 115.658 221.433 118.464C223.84 121.417 228.179 119.428 232.878 121.527L232.108 122.64C231.565 123.425 230.663 123.882 229.708 123.858L194.951 122.951L207.638 103.702V103.703Z'
                    fill='#A2C6F0'
                  />
                </g>
              </g>
              <defs>
                <filter
                  id='filter0_d_4238_41733'
                  x='122.756'
                  y='35.7404'
                  width='131.243'
                  height='105.512'
                  filterUnits='userSpaceOnUse'
                  colorInterpolationFilters='sRGB'
                >
                  <feFlood floodOpacity='0' result='BackgroundImageFix' />
                  <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                  />
                  <feOffset dy='4' />
                  <feGaussianBlur stdDeviation='5' />
                  <feComposite in2='hardAlpha' operator='out' />
                  <feColorMatrix
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.24 0'
                  />
                  <feBlend
                    mode='normal'
                    in2='BackgroundImageFix'
                    result='effect1_dropShadow_4238_41733'
                  />
                  <feBlend
                    mode='normal'
                    in='SourceGraphic'
                    in2='effect1_dropShadow_4238_41733'
                    result='shape'
                  />
                </filter>
                <linearGradient
                  id='paint0_linear_4238_41733'
                  x1='97.4913'
                  y1='28.2811'
                  x2='166.868'
                  y2='175.378'
                  gradientUnits='userSpaceOnUse'
                >
                  <stop stopColor='#FFC444' />
                  <stop offset='1' stopColor='#F36F56' />
                </linearGradient>
                <linearGradient
                  id='paint1_linear_4238_41733'
                  x1='113.679'
                  y1='47.6807'
                  x2='118.045'
                  y2='-96.4149'
                  gradientUnits='userSpaceOnUse'
                >
                  <stop stopColor='#FFC444' />
                  <stop offset='1' stopColor='#F36F56' />
                </linearGradient>
                <linearGradient
                  id='paint2_linear_4238_41733'
                  x1='212.278'
                  y1='115.7'
                  x2='224.422'
                  y2='115.7'
                  gradientUnits='userSpaceOnUse'
                >
                  <stop stopColor='#40447E' />
                  <stop offset='1' stopColor='#3C3B6B' />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      )}
      {step === 3 && teamOpt === 'no' && !acctCreating && (
        <div className='team-create-container'>
          <div className='team-name-form'>
            <div className='team-name-form-label'>Team Name</div>
            <input
              id='searchVal'
              placeholder='Enter your team name...'
              type='text'
              className='team-name-input'
              name='newTeamName'
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
            />
            <div className='team-members-form-section'>
              <div className='team-name-form-label'>Team Members</div>
              <div
                className='team-members-chip-input'
                onClick={() => teamMembersInputRef.current?.focus()}
              >
                <div className='team-members-chip-list'>
                  {teamMembers.map((member, index) => (
                    <div
                      key={`team-member-${index}`}
                      className='team-member-chip'
                    >
                      <div className='team-member-chip-text'>{member}</div>
                      <button
                        type='button'
                        className='team-member-remove-btn'
                        onClick={() => handleRemoveTeamMember(index)}
                        aria-label={`Remove team member ${index + 1}`}
                      >
                        x
                      </button>
                    </div>
                  ))}
                  <input
                    ref={teamMembersInputRef}
                    placeholder={
                      teamMembers.length === 0 ? 'email@example.com' : ''
                    }
                    type='text'
                    className='team-members-chip-input-field'
                    name='teamMemberInput'
                    value={teamMemberInput}
                    onChange={(e) => setTeamMemberInput(e.target.value)}
                    onKeyDown={handleTeamMemberKeyDown}
                  />
                </div>
              </div>
              <div
                className='team-members-add-btn'
                onClick={handleAddTeamMember}
              >
                + Add another member
              </div>
              <div className='team-members-helper-text'>
                Press Enter or click add to append emails into the same field.
              </div>
            </div>
          </div>
        </div>
      )}
      {step === 3 && acctCreating && (
        <div className='setting-up-graphic-container'>
          <div className='setting-up-graphic-wrap'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='184'
              height='179'
              viewBox='0 0 184 179'
              fill='none'
            >
              <g opacity='0.5'>
                <path
                  d='M174.441 156.661L11.2681 155.269V159.387C11.2681 160.505 11.9798 161.455 12.9438 161.625L59.0841 169.734C59.6316 169.831 60.1868 169.856 60.7394 169.81L172.667 160.565C173.666 160.482 174.441 159.529 174.441 158.38V156.661V156.661Z'
                  fill='#D9DADF'
                />
                <path
                  d='M115.226 150.062L12.9555 157.596C11.1251 157.731 9.63721 156.298 9.63721 154.396V78.1894C9.63721 76.2876 11.1251 74.7456 12.9555 74.7456H115.226C116.734 74.7456 117.953 76.1393 117.953 77.8589V146.748C117.953 148.468 116.734 149.952 115.226 150.062Z'
                  fill='#D9DADF'
                />
                <path
                  d='M116.857 150.062L14.5864 157.595C12.756 157.73 11.2681 156.298 11.2681 154.396V78.1891C11.2681 76.2872 12.756 74.7452 14.5864 74.7452H116.857C118.365 74.7452 119.584 76.1389 119.584 77.8586V146.748C119.584 148.468 118.365 149.951 116.857 150.062Z'
                  fill='#F2F6F9'
                />
                <path
                  d='M113.671 145.205L18.4165 151.749C16.9273 151.851 15.7173 150.675 15.7173 149.122V82.4034C15.7173 80.8505 16.9273 79.5867 18.4165 79.5803L113.671 79.1692C114.915 79.1641 115.92 80.3059 115.92 81.7206V142.49C115.92 143.904 114.914 145.12 113.671 145.206V145.205Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M69.3339 76.8287C69.3339 77.5371 68.8075 78.1124 68.1577 78.1143C67.5067 78.1163 66.979 77.5428 66.979 76.8332C66.979 76.1236 67.5074 75.5476 68.1577 75.5476C68.8075 75.5476 69.3339 76.121 69.3339 76.8287Z'
                  fill='white'
                />
                <g opacity='0.48'>
                  <path
                    d='M14.5861 157.595L116.339 150.1L116.857 150.062C117.549 150.011 118.177 149.669 118.658 149.15C111.335 148.749 106.787 146.149 103.854 143.462C97.9619 138.067 98.9841 132.746 92.6305 129.198C84.7059 124.774 78.4749 130.38 72.4618 124.849C68.8164 121.495 70.6059 118.994 66.1203 113.982C61.1518 108.431 57.9172 110.297 52.9237 105.301C46.6104 98.9845 49.7677 94.0696 44.3296 89.5339C37.7319 84.0316 30.6168 89.1241 24.3366 83.8474C21.6278 81.5716 20.1411 78.2626 19.3655 74.744H14.5874C12.757 74.744 11.269 76.286 11.269 78.1879V154.395C11.269 156.296 12.757 157.729 14.5867 157.594L14.5861 157.595Z'
                    fill='white'
                  />
                </g>
                <path
                  d='M120.933 149.644L11.2681 157.574L59.0841 165.977C59.6316 166.074 60.1868 166.099 60.7394 166.053L174.441 156.661L122.677 149.698C122.098 149.62 121.514 149.602 120.933 149.644Z'
                  fill='#F8FAFC'
                />
                <path
                  d='M27.27 157.641L118.569 150.942L146.825 154.712L56.3127 161.874L27.27 157.641Z'
                  fill='#D9DADF'
                />
                <g opacity='0.67'>
                  <path
                    d='M51.1709 108.253C53.1169 104.469 56.7508 101.716 60.9974 101.097L61.2932 82.7574C49.1578 83.3628 38.6182 91.2299 33.8701 102.295L51.1702 108.253L51.1709 108.253Z'
                    fill='white'
                  />
                  <path
                    d='M74.8371 115.777C73.8155 121.876 68.6994 126.731 62.4812 127.035C60.6374 127.125 58.8805 126.806 57.2897 126.155L47.7603 142.396C52.1736 144.564 57.1926 145.624 62.4812 145.282C78.0864 144.27 90.5969 131.358 91.9998 115.827L74.8371 115.777V115.777Z'
                    fill='white'
                  />
                  <path
                    d='M44.8043 140.72L54.3811 124.454C51.5029 122.174 49.6591 118.589 49.6591 114.485C49.6591 113.493 49.7677 112.523 49.9722 111.587L32.6363 105.671C31.717 108.689 31.2212 111.884 31.2212 115.179C31.2212 126.088 36.6305 135.429 44.8043 140.721V140.72Z'
                    fill='white'
                  />
                </g>
                <path
                  d='M64.3776 101.063C70.0527 101.791 74.4475 106.434 74.9714 112.337L103.256 112.505C103.256 112.493 103.256 112.48 103.256 112.467C103.256 90.1176 86.59 71.873 64.867 70.6257L64.377 101.063H64.3776Z'
                  fill='#56A6F3'
                />
              </g>
              <g opacity='0.75'>
                <path
                  d='M9.78369 19.8792V65.9403C9.78369 67.4695 11.0467 68.7091 12.6043 68.7091H107.332C108.832 68.7091 110.047 67.4938 110.047 65.994V20.8336L9.78369 19.8792Z'
                  fill='#F2F6F9'
                />
                <path
                  d='M107.332 15.0718L12.6043 14.0617C11.0467 14.0451 9.78369 15.2712 9.78369 16.8004V21.5202L110.047 22.4427V17.8149C110.047 16.3158 108.832 15.0871 107.332 15.0711V15.0718Z'
                  fill='#EAEDEF'
                />
                <path
                  d='M32.917 44.4968L38.1379 26.5814C46.0376 29.1552 51.7485 36.4833 51.7913 45.0951L32.917 44.4961V44.4968Z'
                  fill='#D9DADF'
                />
                <path
                  d='M46.9114 46.2049C46.6188 50.3015 44.6217 53.9281 41.6138 56.4035L33.4209 45.7632L46.9114 46.2049Z'
                  fill='white'
                />
                <path
                  d='M31.7496 43.8773C28.5891 40.8797 24.7763 37.2568 21.7461 34.3634C24.4345 31.8638 28.0678 30.3455 32.062 30.3756C33.2733 30.3845 34.4501 30.536 35.5765 30.8122L31.7496 43.8773Z'
                  fill='white'
                />
                <path
                  d='M31.0903 44.9979L19.6269 53.2396C18.0234 50.887 17.0874 48.0582 17.0874 45.0177C17.0874 41.2683 18.5108 37.8539 20.8536 35.2667C21.6931 36.0677 22.6258 36.957 23.6487 37.93C26.7466 40.8784 29.8585 43.8306 31.0903 44.9979Z'
                  fill='white'
                />
                <path
                  d='M31.4227 46.3245L31.201 59.7738C26.8368 59.5219 22.9678 57.4244 20.3887 54.2606L31.4227 46.3245Z'
                  fill='white'
                />
                <path
                  d='M32.6931 46.8986L40.6017 57.1661C38.2922 58.7567 35.5003 59.7175 32.481 59.7942L32.6937 46.8986H32.6931Z'
                  fill='white'
                />
                <path
                  d='M103.006 35.4515L54.4634 35.1306V30.5751L103.006 30.9395V35.4515Z'
                  fill='white'
                />
                <path
                  d='M103.006 43.6164L54.4634 43.3748V38.8186L103.006 39.1044V43.6164Z'
                  fill='white'
                />
                <path
                  d='M103.006 51.7814L54.4634 51.6184V47.0629L103.006 47.2694V51.7814Z'
                  fill='white'
                />
                <path
                  d='M103.006 59.9463L54.4634 59.8619V55.3064L103.006 55.4343V59.9463Z'
                  fill='white'
                />
                <path
                  d='M16.2607 17.7893C16.2607 18.4682 15.7004 19.0136 15.0085 19.0065C14.3166 19.0001 13.7563 18.4439 13.7563 17.7644C13.7563 17.0848 14.3173 16.5402 15.0085 16.5472C15.6998 16.5542 16.2607 17.1104 16.2607 17.7893Z'
                  fill='white'
                />
              </g>
              <g filter='url(#filter0_d_4238_40657)'>
                <path
                  d='M134.9 79.968L131.369 85.3693L149.014 96.9189L152.545 91.5176L134.9 79.968Z'
                  fill='white'
                />
                <path
                  d='M151.948 93.5873C152.643 90.6421 150.82 87.691 147.876 86.9959C144.933 86.3008 141.984 88.1248 141.289 91.07C140.594 94.0153 142.417 96.9664 145.361 97.6615C148.304 98.3566 151.253 96.5326 151.948 93.5873Z'
                  fill='url(#paint0_linear_4238_40657)'
                />
                <path
                  d='M146.355 66.0318C150.995 66.0318 154.756 60.7431 154.756 54.2191C154.756 47.6951 150.995 42.4064 146.355 42.4064C141.716 42.4064 137.955 47.6951 137.955 54.2191C137.955 60.7431 141.716 66.0318 146.355 66.0318Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M120.681 25.5957H104.87C88.6584 25.5957 75.5161 38.7465 75.5161 54.9683C75.5161 63.6249 82.529 70.6417 91.1794 70.6417H134.371C143.021 70.6417 150.034 63.6243 150.034 54.9683C150.034 38.7465 136.892 25.5957 120.68 25.5957H120.681Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M117.904 102.759H107.647C97.1293 102.759 88.6035 94.2276 88.6035 83.7036C88.6035 78.0875 93.153 73.5352 98.7655 73.5352H126.787C132.399 73.5352 136.949 78.0875 136.949 83.7036C136.949 94.2282 128.422 102.759 117.905 102.759H117.904Z'
                  fill='var(--text-primary)'
                />
                <path
                  d='M135.032 64.3064H104.978C100.905 64.3064 97.5635 61.1822 97.5635 57.3292V51.4203C97.5635 47.5667 100.905 44.5499 104.978 44.6797L135.032 45.6393C138.669 45.7556 141.585 48.7513 141.585 52.3319V57.8228C141.585 61.4034 138.669 64.3064 135.032 64.3064Z'
                  fill='#4895EF'
                />
                <path
                  d='M114.607 54.0605C114.607 56.6343 112.418 58.6928 109.696 58.6583C106.949 58.6238 104.704 56.4751 104.704 53.8592C104.704 51.2432 106.949 49.186 109.696 49.2633C112.419 49.3394 114.607 51.4874 114.607 54.0605Z'
                  fill='white'
                />
                <path
                  d='M134.917 54.4735C134.917 56.9603 132.873 58.9511 130.331 58.9191C127.767 58.8865 125.671 56.812 125.671 54.2855C125.671 51.7591 127.766 49.7696 130.331 49.8419C132.873 49.9135 134.917 51.9867 134.917 54.4735Z'
                  fill='white'
                />
                <g opacity='0.3'>
                  <path
                    d='M123.682 66.5215C119.212 61.2665 121.199 57.0638 115.355 49.9422C107.315 40.1439 97.9354 41.2524 95.7868 34.6064C95.1914 32.7646 95.1231 30.2375 96.7202 26.7432C84.4743 30.2778 75.5166 41.5752 75.5166 54.9676C75.5166 63.6242 82.5295 70.641 91.1799 70.641H128.821C126.566 69.411 124.918 67.9745 123.682 66.5208V66.5215Z'
                    fill='#F3F4F8'
                  />
                </g>
                <path
                  d='M79.7409 65.95C84.3804 65.95 88.1415 60.6613 88.1415 54.1373C88.1415 47.6133 84.3804 42.3246 79.7409 42.3246C75.1014 42.3246 71.3403 47.6133 71.3403 54.1373C71.3403 60.6613 75.1014 65.95 79.7409 65.95Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M73.0954 59.3027C75.1242 59.3027 76.7689 56.99 76.7689 54.1372C76.7689 51.2844 75.1242 48.9718 73.0954 48.9718C71.0666 48.9718 69.4219 51.2844 69.4219 54.1372C69.4219 56.99 71.0666 59.3027 73.0954 59.3027Z'
                  fill='#001332'
                />
                <path
                  opacity='0.4'
                  d='M120.431 92.2687C119.023 85.6553 103.457 85.8151 103.033 81.4923C102.707 78.1597 111.644 74.8609 112.767 74.446C113.727 74.0912 114.681 73.7914 115.625 73.5344H98.765C93.1525 73.5344 88.603 78.0868 88.603 83.7029C88.603 94.2275 97.1295 102.759 107.647 102.759H117.904C118.89 102.759 119.858 102.685 120.803 102.54C119.91 101.433 119.328 100.222 119.188 98.9006C118.876 95.9708 120.976 94.8316 120.431 92.2687Z'
                  fill='#D1E3F4'
                />
                <path
                  d='M113.028 76.0641C121.975 76.0641 129.228 75.1854 129.228 74.1015C129.228 73.0176 121.975 72.1389 113.028 72.1389C104.081 72.1389 96.8281 73.0176 96.8281 74.1015C96.8281 75.1854 104.081 76.0641 113.028 76.0641Z'
                  fill='#A2C6F0'
                />
                <path
                  d='M109.881 91.5165L94.9356 81.7339C93.4446 80.758 91.4456 81.176 90.4705 82.6675C89.4955 84.159 89.9137 86.1593 91.4047 87.1352L106.35 96.9178L109.881 91.5165Z'
                  fill='white'
                />
                <path
                  d='M107.069 90.5106H122.515L121.902 96.906L106.762 96.3991L107.069 90.5106Z'
                  fill='white'
                />
                <path
                  d='M107.836 96.2082C109.974 94.0684 109.974 90.5991 107.836 88.4593C105.697 86.3195 102.23 86.3195 100.092 88.4593C97.9533 90.5991 97.9533 94.0684 100.092 96.2082C102.23 98.348 105.697 98.348 107.836 96.2082Z'
                  fill='#001332'
                />
                <path
                  d='M123.862 90.3336C122.926 89.8094 121.604 89.4399 119.744 89.5083C118.86 89.5402 118.131 90.2121 118.027 91.0905L117.489 95.6633H125.457L125.717 94.5708C126.114 92.9029 125.357 91.1717 123.862 90.3342V90.3336Z'
                  fill='#001332'
                />
                <path
                  d='M137.932 95.7715L109.187 95.2747C108.792 95.2677 108.418 95.4556 108.187 95.7772L107.31 96.9982C106.874 97.6062 107.294 98.4552 108.042 98.4756L138.468 99.3227L137.932 95.7715H137.932Z'
                  fill='#BEDBF7'
                />
                <path
                  d='M168.786 81.7736L139.147 80.3927C138.27 80.3518 137.437 80.7763 136.953 81.5096L125.512 98.8789L156.857 99.6972C157.718 99.7196 158.531 99.3066 159.021 98.5983L169.565 83.3488C170.014 82.6993 169.574 81.81 168.786 81.7729L168.786 81.7736Z'
                  fill='#BEDBF7'
                />
                <g opacity='0.3'>
                  <path
                    d='M136.953 81.5096C137.436 80.7757 138.27 80.3512 139.147 80.3921L139.797 80.4222C139.831 81.3849 140.066 82.2786 140.604 83.0234C142.284 85.3485 145.406 84.1888 147.513 86.6712C149.827 89.3984 147.332 92.2975 149.394 94.8297C151.565 97.4943 155.478 95.6998 159.716 97.5934L159.021 98.5977C158.532 99.306 157.718 99.719 156.857 99.6966L125.512 98.8783L136.953 81.5089V81.5096Z'
                    fill='#A2C6F0'
                  />
                </g>
              </g>
              <path
                opacity='0.68'
                d='M99.29 118.618V157.042C99.29 158.318 100.243 159.302 101.416 159.24L168.924 155.694C169.935 155.64 170.753 154.64 170.753 153.459V117.887L99.29 118.618Z'
                fill='white'
              />
              <path
                d='M168.925 113.381L101.417 113.73C100.244 113.736 99.291 114.775 99.291 116.05V119.988L170.754 119.155V115.509C170.754 114.329 169.936 113.375 168.925 113.381Z'
                fill='#F8FAFC'
              />
              <path
                d='M104.161 116.781C104.161 117.345 103.741 117.806 103.222 117.811C102.703 117.817 102.282 117.362 102.282 116.797C102.282 116.232 102.703 115.77 103.222 115.766C103.741 115.763 104.161 116.217 104.161 116.781Z'
                fill='white'
              />
              <path
                d='M113.865 153.614L108.554 153.865V125.304L113.865 125.212V153.614Z'
                fill='white'
              />
              <path
                d='M113.865 153.614L108.554 153.865V140.305L113.865 140.129V153.614Z'
                fill='#56A6F3'
              />
              <path
                d='M121.159 153.269L115.928 153.516V125.176L121.159 125.085V153.269Z'
                fill='white'
              />
              <path
                d='M121.159 153.269L115.928 153.516V146.789L121.159 146.579V153.269Z'
                fill='#56A6F3'
              />
              <path
                d='M128.341 152.929L123.19 153.173V125.05L128.341 124.961V152.929Z'
                fill='white'
              />
              <path
                d='M128.341 152.929L123.19 153.173V149.668L128.341 149.444V152.929Z'
                fill='#56A6F3'
              />
              <path
                d='M135.413 152.595L130.341 152.835V124.926L135.413 124.838V152.595Z'
                fill='white'
              />
              <path
                d='M135.413 152.595L130.341 152.835V130.971L135.413 130.85V152.595Z'
                fill='#56A6F3'
              />
              <path
                d='M142.379 152.265L137.383 152.502V124.804L142.379 124.717V152.265Z'
                fill='white'
              />
              <path
                d='M142.379 152.265L137.383 152.502V133.269L142.379 133.137V152.265Z'
                fill='#56A6F3'
              />
              <path
                d='M149.242 151.941L144.32 152.174V124.683L149.242 124.598V151.941Z'
                fill='white'
              />
              <path
                d='M149.242 151.941L144.32 152.174V128.68L149.242 128.574V151.941Z'
                fill='#56A6F3'
              />
              <path
                d='M156.003 151.621L151.154 151.85V124.565L156.003 124.48V151.621Z'
                fill='white'
              />
              <path
                d='M156.003 151.621L151.154 151.85V135.091L156.003 134.95V151.621Z'
                fill='#56A6F3'
              />
              <path
                d='M162.665 151.306L157.887 151.532V124.448L162.665 124.365V151.306Z'
                fill='white'
              />
              <path
                d='M162.665 151.306L157.887 151.532V142.129L162.665 141.953V151.306Z'
                fill='#56A6F3'
              />
              <defs>
                <filter
                  id='filter0_d_4238_40657'
                  x='59.4219'
                  y='19.5957'
                  width='120.323'
                  height='97.1638'
                  filterUnits='userSpaceOnUse'
                  colorInterpolationFilters='sRGB'
                >
                  <feFlood floodOpacity='0' result='BackgroundImageFix' />
                  <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                  />
                  <feOffset dy='4' />
                  <feGaussianBlur stdDeviation='5' />
                  <feComposite in2='hardAlpha' operator='out' />
                  <feColorMatrix
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.24 0'
                  />
                  <feBlend
                    mode='normal'
                    in2='BackgroundImageFix'
                    result='effect1_dropShadow_4238_40657'
                  />
                  <feBlend
                    mode='normal'
                    in='SourceGraphic'
                    in2='effect1_dropShadow_4238_40657'
                    result='shape'
                  />
                </filter>
                <linearGradient
                  id='paint0_linear_4238_40657'
                  x1='141.138'
                  y1='92.3353'
                  x2='152.09'
                  y2='92.3353'
                  gradientUnits='userSpaceOnUse'
                >
                  <stop stopColor='#40447E' />
                  <stop offset='1' stopColor='#3C3B6B' />
                </linearGradient>
              </defs>
            </svg>
          </div>
        </div>
      )}
      {step === 4 && (
        <div className='setting-up-graphic-container'>
          <div className='wizard-trial-ready-panel'>
            <div className='wizard-trial-ready-head'>
              <span className='wizard-trial-ready-bolt-tile'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  aria-hidden='true'
                >
                  <path d='M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z' />
                </svg>
              </span>
              <div className='wizard-trial-ready-head-text'>
                <div className='wizard-trial-ready-plan'>
                  <span className='wizard-trial-ready-plan-name'>
                    Business
                  </span>
                  <span className='msp-inline-active-badge'>Free Trial</span>
                </div>
                <div className='wizard-trial-ready-headline'>
                  Your free {trialDaysLeft ?? 3}-day trial has started
                </div>
              </div>
            </div>
            <div className='wizard-trial-ready-stats'>
              <div className='wizard-trial-ready-stat-card'>
                <div className='wizard-trial-ready-stat-label'>
                  Time remaining
                </div>
                <div className='wizard-trial-ready-stat-value'>
                  {trialDaysLeft !== null ? `${trialDaysLeft} days` : '—'}
                </div>
              </div>
              <div className='wizard-trial-ready-stat-card'>
                <div className='wizard-trial-ready-stat-label'>
                  Trial ends
                </div>
                <div className='wizard-trial-ready-stat-value'>
                  {trialEndLabel || '—'}
                </div>
              </div>
            </div>
            <div className='wizard-trial-ready-list'>
              {TRIAL_INCLUDED_ITEMS.map((item) => (
                <div className='wizard-trial-ready-item' key={item.key}>
                  <div className='wizard-trial-ready-item-icon'>
                    <svg
                      width='20'
                      height='20'
                      viewBox='0 0 24 24'
                      aria-hidden='true'
                      style={{ fill: 'currentColor' }}
                    >
                      <path d={item.iconPath} />
                    </svg>
                  </div>
                  <div className='wizard-trial-ready-item-copy'>
                    <div className='wizard-trial-ready-item-title'>
                      {item.title}
                    </div>
                    <div className='wizard-trial-ready-item-desc'>
                      {item.description}
                    </div>
                  </div>
                  <Link
                    className='wizard-trial-ready-item-link'
                    to={item.linkTo}
                  >
                    {item.linkText} →
                  </Link>
                </div>
              ))}
            </div>
            <Link
              className='wizard-trial-ready-upgrade'
              to={{
                pathname: '/profile',
                state: { linkTab: 'Subscriptions' },
              }}
            >
              Upgrade plan <ArrowForwardIcon fontSize='small' />
            </Link>
          </div>
        </div>
      )}
      <div className='wizard-body-container'>
        <div className='wizard-body-text-group'>
          {step === 1 && (
            <div className='wizard-body-text'>
              We are glad to have you onboard! Our BITE Bot is going to help you
              through the onboarding process.
            </div>
          )}
          {step === 2 && (
            <div className='wizard-body-text'>
              If you received a team invite (via email or link), choose
              &apos;Yes&apos; to join your team. Otherwise, you can create your
              own team and invite others at any time in your account settings.
            </div>
          )}
          {step === 3 && teamOpt === 'yes' && (
            <div className='wizard-body-text'>
              To join your team, go to your email and accept the invite. Return
              to the app and you can start using BITE!
            </div>
          )}
          {step === 3 && teamOpt === 'no' && !acctCreating && (
            <div className='wizard-body-text'>
              You will automatically be set as the admin. Have an invite
              already? Go back and join your team.
            </div>
          )}
          {step === 3 && acctCreating && (
            <div className='wizard-body-text'>
              We are creating your dashboard. Wait one moment while we set
              everything up!
            </div>
          )}
          {step === 4 && (
            <div className='wizard-body-text'>
              Your team is set up. Pick something to try first, or head to
              your dashboard.
            </div>
          )}
        </div>
      </div>
      <div className='wizard-pagination-group'>
        <div
          className={`wizard-page-dot${step === 1 ? '-selected' : ''}`}
        ></div>
        <div
          className={`wizard-page-dot${step === 2 ? '-selected' : ''}`}
        ></div>
        <div
          className={`wizard-page-dot${step === 3 ? '-selected' : ''}`}
        ></div>
        <div
          className={`wizard-page-dot${step === 4 ? '-selected' : ''}`}
        ></div>
      </div>
      <div className='wizard-btn-group'>
        <h3 className='back-to-login' onClick={KeycloakService.doLogout}>
          Back to Login
        </h3>
        <div className='wizard-btn-container'>
          <div className='wizard-btn-dropdown'>
            {acctCreating || step === 4 ? (
              <div className='wizard-back-btn-disabled'>
                <div className='wizard-btn-text-padding'>
                  <div className='wizard-back-btn-text-disabled'>Back</div>
                </div>
              </div>
            ) : (
              <div
                className={`wizard-back-btn${step > 1 ? '' : '-disabled'}`}
                onClick={() => handleStep('decrease')}
              >
                <div className='wizard-btn-text-padding'>
                  <div
                    className={`wizard-back-btn-text${
                      step > 1 ? '' : '-disabled'
                    }`}
                  >
                    Back
                  </div>
                </div>
              </div>
            )}
          </div>
          {step === 1 && (
            <div
              className='wizard-primary-btn'
              onClick={() => handleStep('increase')}
            >
              <div className='wizard-next-btn-text'>Next</div>
            </div>
          )}
          {step === 2 && (
            <>
              {teamOpt === '' ? (
                <div className='wizard-primary-btn-disabled'>
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              ) : (
                <div
                  className='wizard-primary-btn'
                  onClick={() => handleStep('increase')}
                >
                  <div className='wizard-next-btn-text'>Next</div>
                </div>
              )}
            </>
          )}
          {step === 3 && teamOpt === 'yes' && (
            <div className='wizard-primary-btn-disabled'>
              <div className='wizard-next-btn-text'>Done</div>
            </div>
          )}
          {step === 3 &&
            teamOpt === 'no' &&
            newTeamName !== '' &&
            !acctCreating && (
              <div
                className='wizard-primary-btn'
                onClick={() => handleSubmit(newTeamName)}
              >
                <div className='wizard-next-btn-text'>Submit</div>
              </div>
            )}
          {step === 3 &&
            teamOpt === 'no' &&
            newTeamName === '' &&
            !acctCreating && (
              <div className='wizard-primary-btn-disabled'>
                <div className='wizard-next-btn-text'>Submit</div>
              </div>
            )}
          {step === 3 && acctCreating && (
            <div className='wizard-primary-btn-disabled'>
              <div className='wizard-next-btn-text'>Loading...</div>
            </div>
          )}
          {step === 4 && (
            <div
              className='wizard-primary-btn'
              onClick={() => window.location.replace(window.location.origin)}
            >
              <div className='wizard-next-btn-text'>Go to dashboard</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginWizard;
